import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import toast from "react-hot-toast";
import logo from "../../assets/usjp-logo__1_-removebg-preview.png";
import campusGatePhoto from "../../assets/dashboard/campus-gate.jpg";
import avatarPlaceholderIcon from "../../assets/dashboard/icon-avatar-placeholder.jpg";
import activeMembershipIcon from "../../assets/dashboard/icon-active-membership.jpg";
import paymentIcon from "../../assets/dashboard/icon-payment.jpg";
import idCardStatIcon from "../../assets/dashboard/icon-idcard.jpg";
import attendanceIcon from "../../assets/dashboard/icon-attendance.jpg";
import lightningIcon from "../../assets/dashboard/icon-lightning.jpg";
import dateBadgeIcon from "../../assets/dashboard/icon-date-badge.jpg";
import membershipWatermark from "../../assets/dashboard/watermark-membership.jpg";
import paymentWatermark from "../../assets/dashboard/watermark-payment.jpg";
import idCardWatermark from "../../assets/dashboard/watermark-idcard.jpg";
import attendanceWatermark from "../../assets/dashboard/watermark-attendance.jpg";
import {
  getFacilities,
  getFacilityAvailability,
  getClub,
} from "../../services/coachService";
import {
  getMemberAllPayments,
  getMemberRegistrationPayments,
  getMemberBookingPayments,
  getOwnAttendances,
  updateOwnMemberProfile,
} from "../../services/memberService";
import { getMemberMe, memberLogout } from "../../services/authService";
import { submitIdCardReprintRequest, listMyIdCardReprintRequests } from "../../services/idCardReprintService";
import { getMyRenewals, requestRenewal, getMyRenewalShroffVoucherPdf } from "../../services/renewalService";
import { previewBooking, createBooking, getMyBookings, getMyBooking } from "../../services/bookingService";
import { storageUrl } from "../../services/api";
import { getFacilityFallbackImage } from "../../utils/facilityImages";
import { useBookingCart } from "../../hooks/useBookingCart";
import IdCardReprintPaymentModal from "../../components/common/IdCardReprintPaymentModal";
import MembershipRenewalPaymentModal from "../../components/common/MembershipRenewalPaymentModal";
import BookingPaymentModal, {
  rememberBookingPayment,
  getResumableBookingPayment,
  forgetBookingPayment,
} from "../../components/common/BookingPaymentModal";
import OnlinePaymentOnlyModal from "../../components/common/OnlinePaymentOnlyModal";
import BookingTimeLimitModal from "../../components/common/BookingTimeLimitModal";
import { FEATURE_FLAGS } from "../../config/featureFlags";

// Shared dashboard for the two self-booking member roles:
//   • variant="club"        → Club Student (also gets a "My Club" tab)
//   • variant="independent" → Independent member
// Both can book facilities and review their own attendance, payments and
// profile. Wrapped by StudentDashboard / IndependentDashboard.

// ── Design tokens — shared with the LoginPage / CoachDashboard blue family ──
const PRIMARY = "#1d4ed8"; // blue-700
const PRIMARY_DARK = "#1e3a8a"; // blue-900
const NAVY = "#0f1c3f";
const LIGHT = "#e8f0fe";
const SIDEBAR_TOP = "#172554"; // blue-950
const SIDEBAR_BOTTOM = "#1e3a8a"; // blue-900

const todayStr = () => new Date().toISOString().slice(0, 10);

// Client-side hint only for the member/coach booking-date cap
// (MultiEntryBookingService::MEMBER_MAX_BOOKING_DAYS = 30) — the backend is
// authoritative regardless.
const MEMBER_MAX_BOOKING_DAYS = 30;
const maxBookDateStr = () => {
  const d = new Date();
  d.setDate(d.getDate() + MEMBER_MAX_BOOKING_DAYS);
  return d.toISOString().slice(0, 10);
};

const buildName = (initials, denoted, lastname) => {
  const lead = String(initials || denoted || "").trim();
  const tail = String(lastname || "").trim();
  return [lead, tail].filter(Boolean).join(" ") || "Member";
};

const fmtDate = (v) => {
  if (!v) return "—";
  const s = String(v);
  return s.length >= 10 ? s.slice(0, 10) : s;
};

const fmtDateTime = (v) => {
  if (!v) return "—";
  const s = String(v).replace("T", " ");
  return s.length >= 16 ? s.slice(0, 16) : s;
};

const money = (v) =>
  v == null || v === "" ? "—" : `LKR ${Number(v).toLocaleString("en-LK", { minimumFractionDigits: 2 })}`;

// Time-of-day greeting for the dashboard hero banner.
const greetingWord = () => {
  const h = new Date().getHours();
  if (h < 12) return "Good Morning";
  if (h < 17) return "Good Afternoon";
  return "Good Evening";
};

// "Tue, 9 Sep 2025" — the hero banner's date badge.
const todayLongLabel = () =>
  new Date().toLocaleDateString("en-GB", { weekday: "short", day: "numeric", month: "short", year: "numeric" });

// Coarse relative-time label ("Today", "3 days ago", "2 months ago") for the
// Recent Activity feed — only needs day/month granularity, not exact hours.
const timeAgo = (v) => {
  if (!v) return "";
  const d = new Date(String(v).replace(" ", "T"));
  if (Number.isNaN(d.getTime())) return "";
  const days = Math.floor((Date.now() - d.getTime()) / 86400000);
  if (days <= 0) return "Today";
  if (days === 1) return "1 day ago";
  if (days < 30) return `${days} days ago`;
  const months = Math.floor(days / 30);
  return `${months} month${months > 1 ? "s" : ""} ago`;
};

// "14:30" (or "14:30:00") -> "2:30 PM" — only used on the Booking Details
// page, which mirrors a 12-hour design; the rest of the app's booking grids
// intentionally stay 24-hour.
const formatTime12h = (v) => {
  if (!v) return "—";
  const [hStr, mStr] = String(v).slice(0, 5).split(":");
  let h = parseInt(hStr, 10);
  if (Number.isNaN(h)) return "—";
  const period = h >= 12 ? "PM" : "AM";
  h = h % 12 || 12;
  return `${h}:${mStr || "00"} ${period}`;
};

// ── Book Facility tab helpers ────────────────────────────────────────────
// Rotating accent palette for facility cards — cycles by index so it works
// for any number of facilities without hardcoding one color per facility.
const FACILITY_ACCENTS = [
  { bg: "#dbeafe", color: "#1d4ed8" },
  { bg: "#ffedd5", color: "#c2410c" },
  { bg: "#ede9fe", color: "#6d28d9" },
  { bg: "#dcfce7", color: "#15803d" },
  { bg: "#fce7f3", color: "#be185d" },
];
const facilityAccent = (i) => FACILITY_ACCENTS[i % FACILITY_ACCENTS.length];

// The first two time columns of a facility's availability grid, with how
// many of its slot instances are free at each — feeds the "Next Available
// Slots" chips on a facility card.
const previewSlotsFor = (avail) => {
  const timeCols = avail?.slots?.[0]?.time_slots || [];
  const rows = avail?.slots || [];
  return timeCols.slice(0, 2).map((tcol, idx) => ({
    start: String(tcol.start_time).slice(0, 5),
    availableCount: rows.filter((s) => s.time_slots?.[idx]?.available).length,
  }));
};

// Slot codes come back from the backend as e.g. "BASKETBALL-COURT-01" —
// reformat to a friendlier "Basketball Court – 01" for display. Falls back
// to the raw code if it doesn't match that shape. Swimming pool slots are an
// exception: "SWIMMING-POOL-01" becomes "Lane 01" since 01-04 are lanes in
// one pool, not four separate pools.
const prettifySlotCode = (code) => {
  const str = String(code || "");
  const parts = str.split("-");
  const num = parts[parts.length - 1];
  if (parts.length < 2 || !/^\d+$/.test(num)) return str;
  const name = parts
    .slice(0, -1)
    .join(" ")
    .toLowerCase()
    .replace(/\b\w/g, (c) => c.toUpperCase());
  if (name === "Swimming Pool") return `Lane ${num}`;
  return `${name} – ${num}`;
};

// Badge styling + label for one time-slot cell, shared by the facility-card
// preview and the full availability grid. `ts.available` already folds in
// is_blocked/is_full on the backend, but is_blocked is checked first so a
// blocked slot never gets mislabeled "Full".
// A row is selectable if it's neither blocked nor full. Guards against
// undefined so it's safe to call on out-of-bounds neighbors (row before the
// first / after the last time slot).
// The backend only validates that booking_date is today-or-later
// (BookingController::validateBookingRequest — after_or_equal:today), not
// that the time itself hasn't already gone by today, so a row whose start
// time has passed is only ever screened out here, client-side.
const isPastSlot = (dateStr, timeStr) => {
  if (!dateStr || !timeStr) return false;
  const slotStart = new Date(`${dateStr}T${String(timeStr).slice(0, 5)}:00`);
  return !Number.isNaN(slotStart.getTime()) && slotStart.getTime() <= Date.now();
};

// A row is selectable if it's neither blocked, full, nor already past for
// the given booking date.
const rowSelectable = (row, dateStr) =>
  Boolean(row) && Boolean(row.available) && !row.is_blocked && !isPastSlot(dateStr, row.start_time);

// Swatch color for one 30-minute grid cell's status key — matches the
// admin "Book for Member" grid (BookingsManagement.jsx) exactly, so the two
// booking UIs read as one system. "full" (no capacity left) reads as
// "Booked" in the UI since that's what actually produces it in practice;
// "min60" and "past" both collapse into the same muted "Full" swatch (their
// tooltip still explains the real reason).
const SWATCH_COLORS = {
  available: "#22c55e",
  blocked: NAVY,
  full: "#ef4444",
  min60: "#d1d5db",
  past: "#d1d5db",
};

// `hasAvailableNeighbor` means at least one immediately-adjacent row
// (previous or next) is itself selectable — a cell with neither can never
// grow into a valid >=1-hour booking since selection can only ever extend to
// an adjacent cell.
const slotStatus = (ts, hasAvailableNeighbor, isPast) => {
  if (ts.is_blocked) return { key: "blocked", tooltip: `Blocked${ts.block_reason ? `: ${ts.block_reason}` : ""}` };
  if (isPast) return { key: "past", tooltip: "This time slot has already passed" };
  if (!ts.available) return { key: "full", tooltip: "Already booked" };
  if (!hasAvailableNeighbor) return { key: "min60", tooltip: "Not enough consecutive availability for a 1-hour booking" };
  return { key: "available", tooltip: `Available · from LKR ${ts.fee}` };
};

// Given the current selection (or null) and a clicked cell, resolves what
// should happen — mirrors the admin "Book for Member" staff-booking grid
// (BookingsManagement.jsx):
//   - No selection yet, or clicking a different court: start a fresh
//     single-cell selection there (a genuine change of context, not a gap).
//   - Clicking the cell immediately before/after the current range: extend
//     that end by one slot.
//   - Clicking the range's own start/end cell: shrink from that end (or
//     clear entirely if the range is down to one cell).
//   - Clicking a cell already inside the range (not an edge): no-op — can't
//     remove from the middle without breaking contiguity.
//   - Clicking anywhere else in the SAME court, non-adjacent to the current
//     range: blocked (`blocked: true`) rather than silently jumping the
//     selection there — that click would otherwise look like it's bridging
//     over the unselected/unavailable slots in between.
function resolveSelectionClick(prev, slot, idx) {
  if (!prev || prev.facilitySlotId !== slot.id) {
    return { next: { facilitySlotId: slot.id, slotCode: slot.slot_code, startIdx: idx, endIdx: idx } };
  }
  if (idx === prev.startIdx - 1) return { next: { ...prev, startIdx: idx } };
  if (idx === prev.endIdx + 1) return { next: { ...prev, endIdx: idx } };
  if (idx === prev.startIdx) return { next: prev.startIdx === prev.endIdx ? null : { ...prev, startIdx: prev.startIdx + 1 } };
  if (idx === prev.endIdx) return { next: prev.startIdx === prev.endIdx ? null : { ...prev, endIdx: prev.endIdx - 1 } };
  if (idx > prev.startIdx && idx < prev.endIdx) return { next: prev };
  return { next: prev, blocked: true };
}

const formatDuration = (mins) => {
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  if (h && m) return `${h}h ${m}m`;
  if (h) return `${h}h`;
  return `${m}m`;
};

const RENEWAL_WINDOW_DAYS = 7;

// Renewal requests open RENEWAL_WINDOW_DAYS before the membership's expiry
// date, and stay open indefinitely once already expired (backend allows
// requesting any time after expiry — see RenewalView's own copy).
const renewalWindowStartDate = (expiryDate) => {
  if (!expiryDate) return null;
  const expiry = new Date(`${String(expiryDate).slice(0, 10)}T00:00:00`);
  if (Number.isNaN(expiry.getTime())) return null;
  expiry.setDate(expiry.getDate() - RENEWAL_WINDOW_DAYS);
  return `${expiry.getFullYear()}-${String(expiry.getMonth() + 1).padStart(2, "0")}-${String(expiry.getDate()).padStart(2, "0")}`;
};

const isRenewalWindowOpen = (expiryDate) => {
  const opensOn = renewalWindowStartDate(expiryDate);
  if (!opensOn) return true;
  const today = new Date();
  const todayStr = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, "0")}-${String(today.getDate()).padStart(2, "0")}`;
  return todayStr >= opensOn;
};

// Same rule MemberMembershipProgressCard uses to color itself red — expired
// once the end date has passed OR member_status itself already says so.
// Reused here to gate membership-dependent tabs (booking, ID card) once
// expired, since renewal is the only thing that should stay reachable.
const isMemberExpired = (member) => {
  const endStr = member?.membership_end_date;
  if (String(member?.member_status).toLowerCase() === "expired") return true;
  if (!endStr) return false;
  const end = new Date(endStr);
  if (Number.isNaN(end.getTime())) return false;
  return end.getTime() < Date.now();
};

// Nav keys that require an active membership — locked client-side once
// isMemberExpired(member) is true. Renewal itself is deliberately excluded.
const LOCKED_WHEN_EXPIRED = ["book", "idcard"];

const Icon = ({ path, className = "w-5 h-5", stroke = "currentColor", width = 1.8 }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" stroke={stroke}>
    {path.split("|").map((d, i) => (
      <path key={i} strokeLinecap="round" strokeLinejoin="round" strokeWidth={width} d={d} />
    ))}
  </svg>
);

const ICONS = {
  alertCircle: "M12 9v2m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z",
  dashboard: "M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6",
  book: "M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z",
  attendance: "M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7l2 2 4-4",
  payments: "M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z",
  club: "M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0H5m14 0h2M5 21H3m4-14h.01M11 7h.01M15 7h.01M7 11h.01M11 11h.01M15 11h.01M7 15h.01M11 15h.01M15 15h.01",
  profile: "M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z",
  edit: "M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z",
  logout: "M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1",
  refresh: "M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15",
  clock: "M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z",
  check: "M5 13l4 4L19 7",
  card: "M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z",
  bell: "M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9",
  calendar: "M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z",
  userCheck: "M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h7m6-6l2 2 4-4",
  back: "M15 19l-7-7 7-7",
  shield: "M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z",
  upload: "M3 16.5v2.25A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75V16.5M7.5 7.5L12 3m0 0l4.5 4.5M12 3v13.5",
  download: "M3 16.5v2.25A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75V16.5M16.5 12L12 16.5m0 0L7.5 12m4.5 4.5V3",
  facilities: "M3 21h18M5 21V7l8-4v18M19 21V11l-6-3M9 9h.01M9 12h.01M9 15h.01",
  x: "M6 18L18 6M6 6l12 12",
  chevron: "M19 9l-7 7-7-7",
  arrowRight: "M9 5l7 7-7 7",
  info: "M12 8h.01M11 12h1v4h1|M21 12a9 9 0 11-18 0 9 9 0 0118 0z",
  lighting:
    "M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z",
  tag: "M9.568 3H5.25A2.25 2.25 0 003 5.25v4.318c0 .597.237 1.17.659 1.591l9.581 9.581c.699.699 1.78.872 2.607.33a18.095 18.095 0 005.223-5.223c.542-.827.369-1.908-.33-2.607L11.16 3.66A2.25 2.25 0 009.568 3z|M6 6h.008v.008H6V6z",
  lock: "M16.5 10.5V6.75a4.5 4.5 0 10-9 0v3.75m-.75 11.25h10.5a2.25 2.25 0 002.25-2.25v-6.75a2.25 2.25 0 00-2.25-2.25H6.75a2.25 2.25 0 00-2.25 2.25v6.75a2.25 2.25 0 002.25 2.25z",
  fileText: "M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z",
  send: "M6 12L3.269 3.126A59.768 59.768 0 0121.485 12 59.77 59.77 0 013.27 20.876L5.999 12zm0 0h7.5",
  copy: "M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 4h8a2 2 0 012 2v8a2 2 0 01-2 2h-8a2 2 0 01-2-2v-8a2 2 0 012-2z",
  // Sport-type glyphs for the dashboard's Facility Overview tiles.
  dumbbell: "M4 9v6|M2 8v8|M20 9v6|M22 8v8|M9 12h6|M6 9a1 1 0 011-1h1a1 1 0 011 1v6a1 1 0 01-1 1H7a1 1 0 01-1-1V9zM15 9a1 1 0 011-1h1a1 1 0 011 1v6a1 1 0 01-1 1h-1a1 1 0 01-1-1V9z",
  racket: "M14 10a5 5 0 10-4.9 6.02L5 20.14V22h1.86l4.12-4.1A5 5 0 0014 10z|M4.5 19.5l1-1",
  ballSeam: "M12 3a9 9 0 100 18 9 9 0 000-18z|M12 3c3.2 3 3.2 15 0 18|M3 12h18",
  basketball: "M12 3a9 9 0 100 18 9 9 0 000-18z|M3.6 8.6a9 9 0 0016.8 0|M3.6 15.4a9 9 0 0116.8 0|M12 3v18",
};

// Sport-type glyph + accent color for the Facility Overview tiles, matched
// by facility_name keyword — mirrors the same keyword approach as
// getFacilityFallbackImage (utils/facilityImages.js), but resolves to a
// small colored icon badge instead of a stock photo. Falls back to a
// generic facility icon in the PRIMARY blue for anything unrecognized.
const SPORT_TYPE_RULES = [
  { test: /basketball/i, icon: ICONS.basketball, color: "#c2410c", bg: "#ffedd5" },
  { test: /badminton/i, icon: ICONS.racket, color: "#15803d", bg: "#dcfce7" },
  { test: /tennis/i, icon: ICONS.ballSeam, color: "#65a30d", bg: "#ecfccb" },
  { test: /volleyball/i, icon: ICONS.ballSeam, color: "#be185d", bg: "#fce7f3" },
  { test: /swim|pool/i, icon: ICONS.ballSeam, color: "#0891b2", bg: "#cffafe" },
  { test: /gym/i, icon: ICONS.dumbbell, color: PRIMARY, bg: "#dbeafe" },
];
const sportType = (facilityName) =>
  SPORT_TYPE_RULES.find((r) => r.test.test(String(facilityName || ""))) || {
    icon: ICONS.facilities,
    color: PRIMARY,
    bg: LIGHT,
  };

// Opens a blob in a pre-opened tab (must be window.open'd synchronously
// inside the triggering click to avoid the popup blocker) — same pattern as
// the admin pages' openBlobInTab (IdCardReprints.jsx, MembershipRenewals.jsx).
function openBlobInTab(blob, tab) {
  const url = URL.createObjectURL(blob);
  if (tab && !tab.closed) tab.location.href = url;
  else window.open(url, "_blank");
  setTimeout(() => URL.revokeObjectURL(url), 60000);
}

const statusColor = (status) => {
  const s = String(status || "").toLowerCase();
  if (s.includes("active") || s.includes("paid") || s.includes("issued") || s.includes("approved") || s.includes("complete"))
    return { bg: "#dcfce7", color: "#15803d" };
  if (s.includes("pending") || s.includes("await") || s.includes("verification") || s.includes("print"))
    return { bg: "#fef3c7", color: "#b45309" };
  if (s.includes("reject") || s.includes("fail") || s.includes("refund")) return { bg: "#fee2e2", color: "#b91c1c" };
  return { bg: "#e5e7eb", color: "#4b5563" };
};

const Pill = ({ children }) => {
  const c = statusColor(children);
  return (
    <span className="text-xs font-semibold px-2.5 py-0.5 rounded-full whitespace-nowrap" style={{ backgroundColor: c.bg, color: c.color }}>
      {children || "—"}
    </span>
  );
};

const Avatar = ({ name, url, size = 40 }) => {
  const initials = String(name || "?")
    .split(" ")
    .map((p) => p[0])
    .slice(0, 2)
    .join("")
    .toUpperCase();
  return url ? (
    <img src={url} alt={name} className="rounded-full object-cover flex-shrink-0" style={{ width: size, height: size }} />
  ) : (
    <div className="rounded-full flex items-center justify-center flex-shrink-0 font-bold text-white" style={{ width: size, height: size, backgroundColor: PRIMARY, fontSize: size * 0.36 }}>
      {initials}
    </div>
  );
};

export default function MemberDashboard({ variant = "independent" }) {
  const navigate = useNavigate();
  const location = useLocation();

  const isClub = variant === "club";
  const STORAGE_KEY = "sfmis_member";
  const portalLabel = isClub ? "Student Portal" : "Independent Portal";

  // Resolve the logged-in member from navigation state or the session fallback.
  const [member, setMember] = useState(() => {
    const fromState = location.state?.member;
    if (fromState) {
      sessionStorage.setItem(STORAGE_KEY, JSON.stringify(fromState));
      return fromState;
    }
    try {
      return JSON.parse(sessionStorage.getItem(STORAGE_KEY) || "null");
    } catch {
      return null;
    }
  });

  const memberName = member ? buildName(member.initials, member.name_denoted_by_initials, member.lastname) : "";
  // Display name with the title prefixed, e.g. "Mr N. Perera". Used for the
  // visible name headings; the Avatar keeps `memberName` so its initials stay clean.
  const memberFullName = member ? [member.title, memberName].filter(Boolean).join(" ") : "";
  const photoUrl = storageUrl(member?.photo_path) || member?.photo_url || null;

  const [active, setActive] = useState("dashboard");
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const [club, setClub] = useState(null);
  const [facilities, setFacilities] = useState([]);
  const [attendances, setAttendances] = useState([]);
  const [payments, setPayments] = useState([]);
  const [registrationPayments, setRegistrationPayments] = useState([]);
  const [allPayments, setAllPayments] = useState([]);
  const [paymentsDetail, setPaymentsDetail] = useState(null); // null | "booking" | "registration"
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  // ID card reprint (lost card) — fetched lazily when the "idcard" tab opens,
  // same pattern as the Book Facility availability effect below.
  const [reprintRequests, setReprintRequests] = useState([]);
  const [reprintLoading, setReprintLoading] = useState(false);
  const [reprintReason, setReprintReason] = useState("");
  const [reprintFile, setReprintFile] = useState(null);
  const [reprintSubmitting, setReprintSubmitting] = useState(false);
  const [reprintError, setReprintError] = useState("");
  const [payingReprint, setPayingReprint] = useState(null); // the reprint row currently being paid, or null
  // Ref purely for the empty-state "Submit a new request" CTA to focus the
  // existing form's Reason field above it — no new submission behavior.
  const reprintReasonRef = useRef(null);

  // Membership renewal — fetched lazily when the "renewal" tab opens, same
  // pattern as the ID card reprint state above.
  const [renewals, setRenewals] = useState([]);
  const [renewalsLoading, setRenewalsLoading] = useState(false);
  const [requestingRenewal, setRequestingRenewal] = useState(false);
  const [downloadingRenewalVoucher, setDownloadingRenewalVoucher] = useState(null); // renewal_id currently downloading
  const [payingRenewal, setPayingRenewal] = useState(null); // the renewal row currently being paid, or null

  // Book Facility state
  const [selectedFacilityId, setSelectedFacilityId] = useState(null);
  const [bookDate, setBookDate] = useState(todayStr());
  const [availability, setAvailability] = useState(null);
  const [availLoading, setAvailLoading] = useState(false);
  // "facilities" = the card grid (Choose a Facility); "availability" = the
  // per-facility time-slot detail opened via a card's "View Availability".
  const [bookingStep, setBookingStep] = useState("facilities");
  // facilityId -> availability object (or null on fetch failure), used only
  // to render each facility card's "Next Available Slots" preview chips.
  const [facilityAvailabilityMap, setFacilityAvailabilityMap] = useState({});
  // facilityId -> today's facility_available_for_booking boolean, for the
  // Dashboard tab's "Facility Overview" widget only — a lighter-weight
  // sibling of facilityAvailabilityMap above (same endpoint, just today's
  // date and only the first few facilities, not the full grid).
  const [dashboardFacilityAvail, setDashboardFacilityAvail] = useState({});
  // "book" = the choose-a-facility/availability flow above; "mine" = the My
  // Bookings list below. Independent of bookingStep, which stays nested
  // under "book".
  const [bookingView, setBookingView] = useState("book");
  const [myBookings, setMyBookings] = useState([]);
  const [myBookingsLoading, setMyBookingsLoading] = useState(false);
  // The in-progress slot selection on the availability grid: which facility
  // slot (court/column) and which contiguous run of 30-min rows within it
  // (startIdx/endIdx index into that slot's time_slots array, inclusive of
  // both ends). null when nothing selected. This is just the pending
  // grid pick — "Add to Booking" pushes it into the cart below.
  const [selectedRange, setSelectedRange] = useState(null);
  // Which court/slot's row is displayed in the Available Time Slots card —
  // null shows a prompt instead of a grid until the user picks one.
  const [selectedCourtSlotId, setSelectedCourtSlotId] = useState(null);
  const [confirmingBooking, setConfirmingBooking] = useState(false);
  const [payingBooking, setPayingBooking] = useState(null); // POST /bookings response currently being paid
  const [resumingPayment, setResumingPayment] = useState(null); // { response, lockDeadline } — "Pay Now" from Booking Details
  const [showPaymentOnlyNotice, setShowPaymentOnlyNotice] = useState(false); // "Confirm & Pay" -> notice -> handleConfirmBooking
  const [showBookingTimeLimitNotice, setShowBookingTimeLimitNotice] = useState(false); // "View Availability" -> 10-minute notice
  // Multi-entry booking cart — up to 10 dated time-slot entries, all on one
  // facility, built up via "Add to Booking" before a single Confirm & Pay.
  const cart = useBookingCart({ audience: "member", previewFn: previewBooking });
  const [viewingBookingId, setViewingBookingId] = useState(null); // "My Bookings" -> View Details modal
  const [viewingBooking, setViewingBooking] = useState(null);
  const [viewingBookingLoading, setViewingBookingLoading] = useState(false);

  // Profile editing — members may update their address and phone numbers.
  // Held at the top level so the inline ProfileView keeps its state across
  // re-renders. Maps to PUT /api/members/{id} (partial update).
  const [editingProfile, setEditingProfile] = useState(false);
  const [savingProfile, setSavingProfile] = useState(false);
  const [profileForm, setProfileForm] = useState({
    primary_phone: "",
    secondary_phone: "",
    address: "",
  });
  const [profileErrors, setProfileErrors] = useState({});

  // Matches the backend rules: primary must be a mobile (07X XXXXXXX);
  // secondary is any 9-digit local number (optional).
  const isValidPrimaryPhone = (p) => /^(?:0|94|\+94)?7\d{8}$/.test(String(p).replace(/\s/g, ""));
  const isValidSecondaryPhone = (p) => /^(?:0|94|\+94)?\d{9}$/.test(String(p).replace(/\s/g, ""));

  const startEditProfile = () => {
    setProfileForm({
      primary_phone: member.primary_phone_number || "",
      secondary_phone: member.secondary_phone_number || "",
      address: member.address || "",
    });
    setProfileErrors({});
    setEditingProfile(true);
  };

  const cancelEditProfile = () => {
    setEditingProfile(false);
    setProfileErrors({});
  };

  const saveProfile = async () => {
    const errs = {};
    if (!profileForm.primary_phone.trim()) errs.primary_phone = "Primary phone is required.";
    else if (!isValidPrimaryPhone(profileForm.primary_phone))
      errs.primary_phone = "Enter a valid mobile number (e.g. 071 234 5678).";
    if (profileForm.secondary_phone && !isValidSecondaryPhone(profileForm.secondary_phone))
      errs.secondary_phone = "Enter a valid phone number or leave it empty.";
    if (!profileForm.address.trim()) errs.address = "Address is required.";
    if (Object.keys(errs).length) {
      setProfileErrors(errs);
      return;
    }

    setSavingProfile(true);
    try {
      const fd = new FormData();
      fd.append("primary_phone", profileForm.primary_phone.trim());
      fd.append("secondary_phone", profileForm.secondary_phone.trim());
      fd.append("address", profileForm.address.trim());
      const updated = await updateOwnMemberProfile(fd);
      setMember((prev) => {
        const merged = { ...prev, ...updated };
        sessionStorage.setItem(STORAGE_KEY, JSON.stringify(merged));
        return merged;
      });
      setEditingProfile(false);
      toast.success("Profile updated.");
    } catch (err) {
      const data = err?.response?.data;
      const firstError = data?.errors ? Object.values(data.errors)[0]?.[0] : null;
      toast.error(firstError || data?.message || "Could not update your details.");
    } finally {
      setSavingProfile(false);
    }
  };

  useEffect(() => {
    if (!member) navigate("/login", { replace: true });
  }, [member, navigate]);

  // The /verify-otp payload omits the photo (and some fields) — enrich once from
  // the member's own record. /api/member/me works with the member token; the
  // admin /members/{id} endpoint does not.
  useEffect(() => {
    if (!member?.id || member.photo_path) return;
    let on = true;
    getMemberMe()
      .then((full) => {
        if (on && full) {
          setMember((prev) => {
            const merged = { ...prev, ...full };
            sessionStorage.setItem(STORAGE_KEY, JSON.stringify(merged));
            return merged;
          });
        }
      })
      .catch(() => {});
    return () => {
      on = false;
    };
  }, [member?.id, member?.photo_path]);

  // Initial load — facilities, my attendance, my payments, my club (club only).
  useEffect(() => {
    if (!member?.id) return;
    let on = true;
    (async () => {
      setLoading(true);
      const [facs, atts, pays, regPays, allPays, clb] = await Promise.allSettled([
        getFacilities(),
        getOwnAttendances(),
        getMemberBookingPayments(),
        getMemberRegistrationPayments(),
        getMemberAllPayments(),
        isClub && member.club_id ? getClub(member.club_id) : Promise.resolve(null),
      ]);
      if (!on) return;
      const facList = facs.status === "fulfilled" ? facs.value : [];
      setFacilities(facList);
      if (facList.length) setSelectedFacilityId((id) => id ?? facList[0].id);
      setAttendances(atts.status === "fulfilled" && Array.isArray(atts.value) ? atts.value : []);
      setPayments(pays.status === "fulfilled" ? pays.value : []);
      setRegistrationPayments(regPays.status === "fulfilled" ? regPays.value : []);
      setAllPayments(allPays.status === "fulfilled" ? allPays.value : []);
      setClub(clb.status === "fulfilled" ? clb.value : null);
      setLoading(false);
    })();
    return () => {
      on = false;
    };
  }, [member?.id, member?.club_id, isClub]);

  // Load the selected facility's full time-slot grid when the "View
  // Availability" detail step is open.
  useEffect(() => {
    if (active !== "book" || bookingStep !== "availability" || !selectedFacilityId) return;
    let on = true;
    setAvailLoading(true);
    getFacilityAvailability(selectedFacilityId, bookDate)
      .then((data) => on && setAvailability(data))
      .catch(() => {
        if (on) {
          setAvailability(null);
          toast.error("Could not load availability.");
        }
      })
      .finally(() => on && setAvailLoading(false));
    return () => {
      on = false;
    };
  }, [active, bookingStep, selectedFacilityId, bookDate]);

  // Any in-progress slot selection references a specific facility+date's
  // availability grid — clear it the moment either changes so a stale
  // selection can't be confirmed against a grid it no longer matches.
  useEffect(() => {
    setSelectedCourtSlotId(null);
    setSelectedRange(null);
  }, [selectedFacilityId, bookDate]);

  // Derive the actual "H:i" start/end time strings for the current selection
  // from availability + the selected row indices — selectedRange itself only
  // stores indices, so a click that extends/shrinks the run always re-derives
  // against the live grid rather than a stale copy of the times.
  const selectedFacilitySlot = availability?.slots?.find((s) => s.id === selectedRange?.facilitySlotId) || null;
  const selectedRangeRows = selectedFacilitySlot?.time_slots || [];
  const selectedRangeStartRow = selectedRange ? selectedRangeRows[selectedRange.startIdx] : null;
  const selectedRangeEndRow = selectedRange ? selectedRangeRows[selectedRange.endIdx] : null;
  const selectedStartTime = selectedRangeStartRow ? String(selectedRangeStartRow.start_time).slice(0, 5) : null;
  const selectedEndTime = selectedRangeEndRow ? String(selectedRangeEndRow.end_time).slice(0, 5) : null;
  const selectedRangeDurationMinutes = selectedRange ? (selectedRange.endIdx - selectedRange.startIdx + 1) * 30 : 0;

  // Fee/lighting for a pending selection is no longer previewed individually
  // — lighting is fully server-computed now (no client override), and fees
  // are shown per-entry (and as a running total) once "Add to Booking" pushes
  // this selection into the cart, via the cart's own multi-entry preview.

  // Load the member's own bookings when the "My Bookings" view is opened.
  useEffect(() => {
    if (active !== "book" || bookingView !== "mine") return undefined;
    let on = true;
    setMyBookingsLoading(true);
    getMyBookings()
      .then((data) => on && setMyBookings(data))
      .catch(() => on && toast.error("Could not load your bookings."))
      .finally(() => on && setMyBookingsLoading(false));
    return () => {
      on = false;
    };
  }, [active, bookingView]);

  // Load a lightweight availability preview for EVERY facility on the
  // selected date, powering each card's "Next Available Slots" chips on the
  // Choose a Facility grid.
  useEffect(() => {
    if (active !== "book" || bookingStep !== "facilities" || facilities.length === 0) return;
    let on = true;
    Promise.allSettled(facilities.map((f) => getFacilityAvailability(f.id, bookDate))).then((results) => {
      if (!on) return;
      const map = {};
      results.forEach((res, i) => {
        map[facilities[i].id] = res.status === "fulfilled" ? res.value : null;
      });
      setFacilityAvailabilityMap(map);
    });
    return () => {
      on = false;
    };
  }, [active, bookingStep, facilities, bookDate]);

  // Same idea, scoped to the Dashboard tab's "Facility Overview" widget —
  // just today's availability for the handful of facilities shown there.
  useEffect(() => {
    if (active !== "dashboard" || facilities.length === 0) return;
    let on = true;
    const shown = facilities.slice(0, 4);
    Promise.allSettled(shown.map((f) => getFacilityAvailability(f.id, todayStr()))).then((results) => {
      if (!on) return;
      const map = {};
      results.forEach((res, i) => {
        map[shown[i].id] = res.status === "fulfilled" ? Boolean(res.value?.facility_available_for_booking) : null;
      });
      setDashboardFacilityAvail(map);
    });
    return () => {
      on = false;
    };
  }, [active, facilities]);

  const fetchReprintRequests = useCallback(() => {
    setReprintLoading(true);
    return listMyIdCardReprintRequests()
      .then(setReprintRequests)
      .catch(() => toast.error("Could not load your ID card reprint requests."))
      .finally(() => setReprintLoading(false));
  }, []);

  // ID card reprint requests when the "idcard" tab is open — same lazy,
  // per-tab pattern as the Book Facility availability effect above.
  useEffect(() => {
    if (active !== "idcard") return;
    fetchReprintRequests();
  }, [active, fetchReprintRequests]);

  const fetchRenewals = useCallback(() => {
    setRenewalsLoading(true);
    return getMyRenewals()
      .then(setRenewals)
      .catch(() => toast.error("Could not load your membership renewals."))
      .finally(() => setRenewalsLoading(false));
  }, []);

  // Renewal history when the "renewal" tab is open — same lazy, per-tab
  // pattern as the ID card reprint effect above.
  useEffect(() => {
    if (active !== "renewal") return;
    fetchRenewals();
  }, [active, fetchRenewals]);

  const handleRequestRenewal = async () => {
    if (requestingRenewal) return;
    setRequestingRenewal(true);
    try {
      const r = await requestRenewal();
      toast.success(r.message || "Renewal requested.");
      fetchRenewals();
      // store() only ever returns a Pending renewal (new, or the existing
      // pending one it dedupes onto) — never a Paid one — so it's always
      // safe to offer payment right away.
      if (r.renewal) setPayingRenewal(r.renewal);
    } catch (err) {
      toast.error(err?.response?.data?.message || "Could not request a renewal.");
    } finally {
      setRequestingRenewal(false);
    }
  };

  const handleDownloadRenewalVoucher = async (renewalId) => {
    if (downloadingRenewalVoucher) return;
    const tab = window.open("", "_blank");
    setDownloadingRenewalVoucher(renewalId);
    try {
      const blob = await getMyRenewalShroffVoucherPdf(renewalId);
      openBlobInTab(blob, tab);
    } catch (err) {
      tab?.close?.();
      toast.error(err?.response?.data?.message || "Could not load the voucher.");
    } finally {
      setDownloadingRenewalVoucher(null);
    }
  };

  const handleSubmitReprint = async (e) => {
    e.preventDefault();
    if (reprintSubmitting || !reprintFile || !reprintReason.trim()) return;
    setReprintSubmitting(true);
    setReprintError("");
    try {
      const r = await submitIdCardReprintRequest({ reason: reprintReason.trim(), police_report: reprintFile });
      toast.success(r.message || "Reprint request submitted.");
      setReprintReason("");
      setReprintFile(null);
      fetchReprintRequests();
    } catch (err) {
      setReprintError(err?.response?.data?.message || "Could not submit the reprint request.");
    } finally {
      setReprintSubmitting(false);
    }
  };

  const handleLogout = () => {
    memberLogout();
    sessionStorage.removeItem(STORAGE_KEY);
    navigate("/", { replace: true });
  };

  const handleRefresh = useCallback(async () => {
    if (!member?.id || refreshing) return;
    setRefreshing(true);
    const tasks = [
      getFacilities().then(setFacilities).catch(() => {}),
      getOwnAttendances()
        .then((d) => setAttendances(Array.isArray(d) ? d : []))
        .catch(() => {}),
      getMemberBookingPayments().then(setPayments).catch(() => {}),
      getMemberRegistrationPayments().then(setRegistrationPayments).catch(() => {}),
      getMemberAllPayments().then(setAllPayments).catch(() => {}),
    ];
    if (isClub && member.club_id) tasks.push(getClub(member.club_id).then(setClub).catch(() => {}));
    if (active === "book" && bookingStep === "availability" && selectedFacilityId) {
      tasks.push(getFacilityAvailability(selectedFacilityId, bookDate).then(setAvailability).catch(() => {}));
    }
    if (active === "book" && bookingStep === "facilities" && facilities.length) {
      tasks.push(
        Promise.allSettled(facilities.map((f) => getFacilityAvailability(f.id, bookDate))).then((results) => {
          const map = {};
          results.forEach((res, i) => {
            map[facilities[i].id] = res.status === "fulfilled" ? res.value : null;
          });
          setFacilityAvailabilityMap(map);
        })
      );
    }
    if (active === "dashboard" && facilities.length) {
      const shown = facilities.slice(0, 4);
      tasks.push(
        Promise.allSettled(shown.map((f) => getFacilityAvailability(f.id, todayStr()))).then((results) => {
          const map = {};
          results.forEach((res, i) => {
            map[shown[i].id] = res.status === "fulfilled" ? Boolean(res.value?.facility_available_for_booking) : null;
          });
          setDashboardFacilityAvail(map);
        })
      );
    }
    if (active === "book" && bookingView === "mine") {
      tasks.push(getMyBookings().then(setMyBookings).catch(() => {}));
    }
    if (active === "idcard") {
      tasks.push(listMyIdCardReprintRequests().then(setReprintRequests).catch(() => {}));
    }
    if (active === "renewal") {
      tasks.push(getMyRenewals().then(setRenewals).catch(() => {}));
    }
    try {
      await Promise.all(tasks);
      toast.success("Dashboard refreshed.");
    } finally {
      setRefreshing(false);
    }
  }, [member, refreshing, isClub, active, bookingStep, bookingView, selectedFacilityId, bookDate, facilities]);

  // Most recent scan, by date — attendances isn't guaranteed to arrive
  // sorted, so this is derived rather than just taking attendances[0].
  const latestAttendance = useMemo(() => {
    if (!attendances.length) return null;
    return attendances.reduce((latest, a) => {
      if (!a.scanned_at) return latest;
      return !latest || new Date(a.scanned_at) > new Date(latest.scanned_at) ? a : latest;
    }, null);
  }, [attendances]);

  // The four dashboard hero stat cards — same underlying fields as the old
  // `stats` array (member_status/payment_status/card_status/attendance
  // count), just with a richer image icon + honest one-line subtext instead
  // of a bare SVG glyph.
  const heroStats = useMemo(
    () => [
      {
        key: "membership",
        tab: "profile",
        label: "Active Membership",
        value: member?.member_status || "—",
        sub: member?.membership_end_date ? `Valid until ${fmtDate(member.membership_end_date)}` : "No membership on file",
        positive: /active/i.test(member?.member_status || ""),
        iconSrc: activeMembershipIcon,
        watermark: membershipWatermark,
        bg: "linear-gradient(135deg,#e8fbf0,#ffffff)",
        ring: "#16a34a",
      },
      {
        key: "payment",
        tab: "payments",
        label: "Payment",
        value: member?.payment_status || "—",
        sub: /paid/i.test(member?.payment_status || "") ? "All dues settled" : "Payment required",
        positive: /paid/i.test(member?.payment_status || ""),
        iconSrc: paymentIcon,
        watermark: paymentWatermark,
        bg: "linear-gradient(135deg,#eaf2ff,#ffffff)",
        ring: PRIMARY,
      },
      {
        key: "idcard",
        tab: "idcard",
        label: "ID Card",
        value: member?.card_status || "—",
        sub: /issu|print|ready/i.test(member?.card_status || "") ? "Ready" : "In progress",
        positive: /issu|print|ready/i.test(member?.card_status || ""),
        iconSrc: idCardStatIcon,
        watermark: idCardWatermark,
        bg: "linear-gradient(135deg,#f3ecff,#ffffff)",
        ring: "#7c3aed",
      },
      {
        key: "attendance",
        tab: "attendance",
        label: "My Attendance",
        value: attendances.length,
        sub: latestAttendance ? `Last: ${fmtDate(latestAttendance.scanned_at)}` : "No attendance yet",
        positive: attendances.length > 0,
        iconSrc: attendanceIcon,
        watermark: attendanceWatermark,
        bg: "linear-gradient(135deg,#fff6e6,#ffffff)",
        ring: "#d97706",
      },
    ],
    [member, attendances.length, latestAttendance]
  );

  // Recent Activity feed — built from data already on hand (own attendance
  // scans + own payments) rather than a dedicated activity-log endpoint,
  // which this portal doesn't have. Newest first, capped to a handful.
  const recentActivity = useMemo(() => {
    const items = [];
    attendances.forEach((a) => {
      if (a.scanned_at) items.push({ id: `att-${a.id}`, date: a.scanned_at, type: "attendance", title: "Facility attendance recorded", detail: a.scanned_code ? `Code ${a.scanned_code}` : null });
    });
    allPayments.forEach((p) => {
      const date = p.paid_at || p.created_at;
      if (!date) return;
      const isPaid = /paid|complete/i.test(p.payment_status || "");
      items.push({
        id: `pay-${p.id ?? p.process_id ?? p.payment_id ?? date}`,
        date,
        type: "payment",
        title: isPaid ? "Payment completed" : `Payment ${String(p.payment_status || "updated").toLowerCase()}`,
        detail: money(p.paid_amount ?? p.amount),
      });
    });
    return items
      .sort((a, b) => new Date(b.date) - new Date(a.date))
      .slice(0, 4);
  }, [attendances, allPayments]);

  // Booking and ID card are membership-gated — both require an active
  // membership on the backend, so lock them client-side too once expired
  // rather than letting the member hit a 4xx deep into the flow. Renewal
  // stays reachable regardless — it's the only way out of this state.
  const membershipExpired = isMemberExpired(member);

  const NAV = useMemo(
    () => [
      { key: "dashboard", label: "Dashboard", icon: ICONS.dashboard },
      { key: "book", label: "Book Facility", icon: ICONS.book },
      { key: "attendance", label: "My Attendance", icon: ICONS.attendance },
      { key: "payments", label: "Payments", icon: ICONS.payments },
      ...(isClub ? [{ key: "club", label: "My Club", icon: ICONS.club }] : []),
      { key: "idcard", label: "ID Card", icon: ICONS.shield },
      { key: "renewal", label: "Renew Membership", icon: ICONS.refresh },
      { key: "profile", label: "Profile", icon: ICONS.profile },
    ]
      // Booking UI hidden entirely via FEATURE_FLAGS.bookingUI (frontend-only).
      .filter((n) => n.key !== "book" || FEATURE_FLAGS.bookingUI)
      .map((n) => ({
      ...n,
      locked: (membershipExpired && LOCKED_WHEN_EXPIRED.includes(n.key)) || (n.key === "book" && !FEATURE_FLAGS.facilityBooking),
    })),
    [isClub, membershipExpired]
  );

  const VIEW_TITLES = {
    dashboard: "Dashboard",
    book: "Book Facility",
    attendance: "My Attendance",
    payments: "Payments",
    club: "My Club",
    idcard: "ID Card",
    renewal: "Renew Membership",
    profile: "Profile",
  };

  if (!member) return null;

  const selectedFacility = facilities.find((f) => f.id === selectedFacilityId);

  // ── Book Facility: slot selection + create/pay handlers ──────────────────
  // Click any available cell to select it, click an adjacent cell to extend
  // the run, click the run's own start/end cell to shrink it — see
  // resolveSelectionClick() above for the full rule set.
  const handleCellClick = (slot, idx) => {
    const rows = slot.time_slots || [];
    if (!rowSelectable(rows[idx], bookDate)) return;
    const { next, blocked } = resolveSelectionClick(selectedRange, slot, idx);
    if (blocked) {
      toast.error("Slots must be consecutive. Extend from the edge of your current selection, or clear it first.");
      return;
    }
    setSelectedRange(next);
  };

  const clearSelection = () => setSelectedRange(null);

  // Pushes the current grid selection into the cart, then clears the grid
  // selection so the member can immediately pick another date/slot without
  // leaving the page — this is what replaces the old "book one slot at a
  // time" flow with a multi-entry checkout.
  const handleAddToCart = () => {
    if (!selectedRange || selectedRangeDurationMinutes < 60 || !selectedStartTime || !selectedEndTime) return;
    const result = cart.addEntry({
      facility_id: selectedFacilityId,
      facility_slot_id: selectedRange.facilitySlotId,
      slot_code: selectedRange.slotCode,
      booking_date: bookDate,
      start_time: selectedStartTime,
      end_time: selectedEndTime,
    });
    if (!result.ok) {
      toast.error(
        result.reason === "max_entries"
          ? "You can add up to 10 time slots per booking."
          : "All time slots in one booking must be at the same facility."
      );
      return;
    }
    setSelectedRange(null);
  };

  // Confirms the whole cart in one checkout. Snapshots cart.entries so a
  // 409's positional entry_index can be mapped back to the right cart row
  // even though the entries array is asynchronous state.
  const handleConfirmBooking = async () => {
    if (cart.entries.length === 0 || confirmingBooking) return;
    const submittedEntries = cart.entries;
    setConfirmingBooking(true);
    try {
      const res = await createBooking({
        facility_id: cart.facilityId,
        entries: submittedEntries.map(({ facility_slot_id, booking_date, start_time, end_time }) => ({
          facility_slot_id,
          booking_date,
          start_time,
          end_time,
        })),
      });
      rememberBookingPayment(res);
      setPayingBooking(res);
    } catch (err) {
      const data = err?.response?.data;
      if (data?.error_code === "booking_entries_unavailable" || data?.error_code === "booking_entry_blocked") {
        cart.applyConflicts(data.conflicts, submittedEntries);
        toast.error(
          data.conflicts?.length === 1
            ? data.conflicts[0].user_message || data.message
            : `${data.conflicts?.length ?? "Some"} time slots in your booking are no longer available — see below.`
        );
        if (selectedFacilityId) {
          getFacilityAvailability(selectedFacilityId, bookDate).then(setAvailability).catch(() => {});
        }
      } else if (data?.error_code === "mixed_facilities") {
        toast.error("Your booking mixed two different facilities unexpectedly — please start again.");
        cart.clearCart();
      } else {
        toast.error(data?.message || "Could not create the booking. It may no longer be available.");
      }
    } finally {
      setConfirmingBooking(false);
    }
  };

  const refreshMyBookings = () => {
    setMyBookingsLoading(true);
    getMyBookings().then(setMyBookings).catch(() => {}).finally(() => setMyBookingsLoading(false));
  };

  const handleBookingPaid = () => {
    forgetBookingPayment(payingBooking?.booking?.id ?? payingBooking?.booking_id);
    setPayingBooking(null);
    cart.clearCart();
    setSelectedRange(null);
    toast.success("Booking paid and confirmed!");
    if (selectedFacilityId) {
      getFacilityAvailability(selectedFacilityId, bookDate).then(setAvailability).catch(() => {});
    }
    setBookingView("mine");
    refreshMyBookings();
  };

  const handleBookingExpired = () => {
    forgetBookingPayment(payingBooking?.booking?.id ?? payingBooking?.booking_id);
    setPayingBooking(null);
    setSelectedRange(null);
    if (selectedFacilityId) {
      getFacilityAvailability(selectedFacilityId, bookDate).then(setAvailability).catch(() => {});
    }
  };

  // Facility-switch policy: a non-empty cart can only ever be for one
  // facility, so browsing a different one means starting over. Confirms
  // (via a toast action) instead of silently blocking the facility picker.
  const goToFacilities = () => {
    if (cart.entries.length > 0) {
      toast(
        (t) => (
          <span className="text-sm">
            Switching facilities will clear your {cart.entries.length}-slot booking in progress.{" "}
            <button
              type="button"
              className="font-bold underline"
              onClick={() => {
                toast.dismiss(t.id);
                cart.clearCart();
                setSelectedRange(null);
                setBookingStep("facilities");
              }}
            >
              Switch anyway
            </button>
          </span>
        ),
        { duration: 6000 }
      );
      return;
    }
    setBookingStep("facilities");
  };

  const openFacilityAvailability = (facilityId) => {
    if (!FEATURE_FLAGS.bookingUI) return;
    if (cart.entries.length > 0 && facilityId !== cart.facilityId) {
      toast(
        (t) => (
          <span className="text-sm">
            Switching facilities will clear your {cart.entries.length}-slot booking in progress.{" "}
            <button
              type="button"
              className="font-bold underline"
              onClick={() => {
                toast.dismiss(t.id);
                cart.clearCart();
                setSelectedFacilityId(facilityId);
                setBookingStep("availability");
                setShowBookingTimeLimitNotice(true);
              }}
            >
              Switch anyway
            </button>
          </span>
        ),
        { duration: 6000 }
      );
      return;
    }
    setSelectedFacilityId(facilityId);
    setBookingStep("availability");
    setShowBookingTimeLimitNotice(true);
  };

  // ════════════════════════════════════════════ VIEWS ════════════════════════
  const DashboardView = () => (
    <>
      {/* Hero welcome banner */}
      <div className="relative overflow-hidden rounded-2xl mb-6 shadow-sm" style={{ background: "linear-gradient(120deg, #eafaf1 0%, #eef5ff 55%, #ffffff 100%)" }}>
        <div className="flex flex-col lg:flex-row items-stretch">
          <div className="flex-1 min-w-0 p-6 sm:p-8 flex flex-col justify-center">
            <h2 className="text-xl sm:text-2xl font-extrabold flex items-center gap-2" style={{ color: NAVY }}>
              <span>👋</span> {greetingWord()}, {memberFullName}
            </h2>
            <p className="text-sm text-gray-600 mt-1">Welcome back to USJ Sports Federation</p>
            <p className="text-xs text-gray-500 mt-3 flex items-center gap-2 flex-wrap">
              <span>Stay active</span><span className="text-gray-300">•</span>
              <span>Stay healthy</span><span className="text-gray-300">•</span>
              <span>Achieve your goals</span>
            </p>
          </div>
          <div className="relative lg:w-[42%] min-h-[170px]">
            <img src={campusGatePhoto} alt="University of Sri Jayewardenepura" className="absolute inset-0 w-full h-full object-cover" />
            <div className="absolute inset-0" style={{ background: "linear-gradient(90deg, #eafaf1 0%, rgba(234,250,241,0) 40%)" }} />
            <div className="absolute top-4 right-4 bg-white/95 backdrop-blur rounded-xl shadow-md pl-2 pr-3 py-2 flex items-center gap-2">
              <img src={dateBadgeIcon} alt="" className="w-8 h-8 rounded-full object-cover flex-shrink-0" />
              <div className="leading-tight">
                <p className="text-xs font-bold whitespace-nowrap" style={{ color: NAVY }}>{todayLongLabel()}</p>
                <p className="text-[10px] text-gray-400">Today</p>
              </div>
            </div>
            <p className="absolute bottom-4 right-5 text-white font-semibold italic text-sm" style={{ fontFamily: "Georgia, serif", textShadow: "0 1px 4px rgba(0,0,0,0.5)" }}>
              Play · Train · Grow
            </p>
          </div>
        </div>
      </div>

      {/* Stat cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        {heroStats.map((s) => (
          <div
            key={s.key}
            className="relative overflow-hidden rounded-2xl p-5 shadow-sm"
            style={{ background: s.bg, border: "1px solid rgba(0,0,0,0.04)" }}
          >
            {/* Decorative icon peeking from the corner, like the reference cards */}
            <img
              src={s.watermark}
              alt=""
              aria-hidden="true"
              className="absolute -right-2 -bottom-2 w-16 h-16 rounded-xl object-cover opacity-60 mix-blend-multiply pointer-events-none select-none"
            />

            <div className="relative z-10">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2 min-w-0">
                  <img src={s.iconSrc} alt="" className="w-8 h-8 rounded-full object-cover flex-shrink-0" />
                  <p className="font-bold text-xs truncate" style={{ color: NAVY }}>{s.label}</p>
                </div>
                <button onClick={() => setActive(s.tab)} className="p-1 rounded-full hover:bg-black/10 flex-shrink-0" aria-label={`View ${s.label}`}>
                  <Icon path={ICONS.chevron} className="w-4 h-4 -rotate-90" stroke="#9ca3af" width={2} />
                </button>
              </div>
              <p className="font-extrabold truncate text-2xl mb-2" style={{ color: NAVY }}>{s.value}</p>
              <p className="text-[11px] font-semibold flex items-center gap-1 truncate" style={{ color: s.positive ? s.ring : "#9ca3af" }}>
                <Icon path={s.positive ? ICONS.check : ICONS.clock} className="w-3 h-3 flex-shrink-0" stroke={s.positive ? s.ring : "#9ca3af"} width={2.5} />
                {s.sub}
              </p>
            </div>
          </div>
        ))}
      </div>

      <div className="mb-6">
        <MemberMembershipProgressCard />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6">
        {/* Member information */}
        <div className="lg:col-span-2 bg-white rounded-2xl shadow-sm p-6">
          <div className="flex items-center justify-between mb-5">
            <div className="flex items-center gap-2">
              <Icon path={ICONS.profile} className="w-5 h-5" stroke={PRIMARY} width={2} />
              <h3 className="font-bold text-sm" style={{ color: NAVY }}>Member Information</h3>
            </div>
            <button
              onClick={() => setActive("profile")}
              className="flex items-center gap-1.5 text-xs font-semibold border border-gray-200 rounded-lg px-3 py-1.5 hover:bg-gray-50 transition"
              style={{ color: PRIMARY }}
            >
              <Icon path={ICONS.profile} className="w-3.5 h-3.5" stroke={PRIMARY} width={2} /> View Profile
            </button>
          </div>
          <div className="flex items-center gap-4 mb-5">
            {photoUrl ? (
              <Avatar name={memberName} url={photoUrl} size={64} />
            ) : (
              <img src={avatarPlaceholderIcon} alt="" className="w-16 h-16 rounded-full object-cover flex-shrink-0" />
            )}
            <div className="min-w-0">
              <h4 className="font-bold text-lg truncate" style={{ color: NAVY }}>{memberFullName}</h4>
              <p className="text-sm text-gray-400">{member.member_type || (isClub ? "Club Student" : "Independent")} · {member.member_code || member.id || "—"}</p>
              <span className="inline-block mt-1"><Pill>{member.member_status}</Pill></span>
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-y-2 gap-x-6 pt-4 border-t border-gray-100">
            <InfoRow label="Email" value={member.email} />
            <InfoRow label="Gender" value={member.member_gender} />
            <InfoRow label="Payment Status" value={<Pill>{member.payment_status}</Pill>} />
            <InfoRow label="Membership Start" value={fmtDate(member.membership_start_date)} />
            <InfoRow label="Membership End" value={fmtDate(member.membership_end_date)} />
            {isClub && <InfoRow label="Club" value={member.club_name || member.requested_club_name} />}
          </div>
        </div>

        {/* Quick actions */}
        <div className="bg-white rounded-2xl shadow-sm p-5">
          <div className="flex items-center gap-2.5 mb-4">
            <img src={lightningIcon} alt="" className="w-9 h-9 rounded-lg object-cover flex-shrink-0" />
            <div>
              <h3 className="font-bold text-sm" style={{ color: NAVY }}>Quick Actions</h3>
              <p className="text-[11px] text-gray-400">Get things done quickly</p>
            </div>
          </div>
          {FEATURE_FLAGS.bookingUI && (
            <button
              onClick={() => setActive("book")}
              className="w-full flex items-center gap-3 px-4 py-3 rounded-xl mb-2 text-left transition hover:opacity-90"
              style={{ backgroundColor: PRIMARY }}
            >
              <Icon path={ICONS.book} className="w-5 h-5 flex-shrink-0" stroke="#fff" width={1.8} />
              <span className="min-w-0 flex-1">
                <p className="text-sm font-semibold text-white">Book a Facility</p>
                <p className="text-[11px] text-blue-100 truncate">Reserve your preferred facility slot</p>
              </span>
              <Icon path={ICONS.chevron} className="w-4 h-4 -rotate-90 flex-shrink-0" stroke="#fff" width={2} />
            </button>
          )}
          <button
            onClick={() => setActive("attendance")}
            className="w-full flex items-center gap-3 px-4 py-3 rounded-xl mb-2 text-left transition hover:opacity-80"
            style={{ backgroundColor: LIGHT }}
          >
            <Icon path={ICONS.attendance} className="w-5 h-5 flex-shrink-0" stroke={PRIMARY} width={1.8} />
            <span className="min-w-0 flex-1">
              <p className="text-sm font-semibold" style={{ color: NAVY }}>View My Attendance</p>
              <p className="text-[11px] text-gray-500 truncate">Check your attendance records</p>
            </span>
            <Icon path={ICONS.chevron} className="w-4 h-4 -rotate-90 flex-shrink-0" stroke="#9ca3af" width={2} />
          </button>
          <button
            onClick={() => setActive("payments")}
            className="w-full flex items-center gap-3 px-4 py-3 rounded-xl text-left transition hover:opacity-80"
            style={{ backgroundColor: "#f3ecff" }}
          >
            <Icon path={ICONS.payments} className="w-5 h-5 flex-shrink-0" stroke="#7c3aed" width={1.8} />
            <span className="min-w-0 flex-1">
              <p className="text-sm font-semibold" style={{ color: NAVY }}>View My Payments</p>
              <p className="text-[11px] text-gray-500 truncate">Check your payment history</p>
            </span>
            <Icon path={ICONS.chevron} className="w-4 h-4 -rotate-90 flex-shrink-0" stroke="#9ca3af" width={2} />
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* "Your Fitness, Our Priority" banner */}
        <div
          className="relative overflow-hidden rounded-2xl shadow-sm flex min-h-[220px]"
          style={{ background: "linear-gradient(120deg, #eef3ff 0%, #f6f4ff 60%, #ffffff 100%)" }}
        >
          <div className="relative w-2/5 min-w-[110px] flex-shrink-0">
            <img src={campusGatePhoto} alt="University of Sri Jayewardenepura" className="absolute inset-0 w-full h-full object-cover" />
            <div className="absolute inset-0" style={{ background: "linear-gradient(90deg, rgba(238,243,255,0) 55%, #eef3ff 100%)" }} />
          </div>
          <div className="flex-1 min-w-0 p-5 flex flex-col justify-center">
            <p className="text-[10px] font-bold tracking-wide mb-1" style={{ color: PRIMARY }}>USJ SPORTS FEDERATION</p>
            <h3 className="font-extrabold text-base sm:text-lg leading-tight mb-1.5" style={{ color: NAVY }}>Your Fitness, Our Priority</h3>
            <p className="text-xs text-gray-500 mb-4">
              Stay active, stay healthy. Explore and enjoy the facilities at the University of Sri Jayewardenepura.
            </p>
            {FEATURE_FLAGS.bookingUI && (
              <button
                onClick={() => setActive("book")}
                className="self-start inline-flex items-center gap-1.5 text-xs font-semibold text-white rounded-lg px-3.5 py-2.5 transition hover:opacity-90"
                style={{ backgroundColor: NAVY }}
              >
                Explore Facilities <Icon path={ICONS.arrowRight} className="w-3.5 h-3.5" stroke="#fff" width={2.2} />
              </button>
            )}
          </div>
        </div>

        {/* Facility overview */}
        <div className="bg-white rounded-2xl shadow-sm p-5">
          <div className="flex items-center gap-2 mb-1">
            <Icon path={ICONS.facilities} className="w-5 h-5" stroke={PRIMARY} width={1.8} />
            <h3 className="font-bold text-sm" style={{ color: NAVY }}>Facility Overview</h3>
          </div>
          <p className="text-[11px] text-gray-400 mb-4">Explore our world-class sports facilities</p>
          {facilities.length === 0 ? (
            <p className="text-xs text-gray-400 py-6 text-center">No facilities configured yet.</p>
          ) : (
            <div className="grid grid-cols-2 gap-3 mb-4">
              {facilities.slice(0, 4).map((f) => {
                const sport = sportType(f.facility_name);
                const avail = dashboardFacilityAvail[f.id];
                return (
                  <button
                    key={f.id}
                    onClick={() => openFacilityAvailability(f.id)}
                    disabled={!FEATURE_FLAGS.bookingUI}
                    className="rounded-xl border border-gray-100 p-2.5 flex flex-col items-center text-center gap-1.5 enabled:hover:shadow-sm disabled:cursor-default transition"
                  >
                    <div className="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0" style={{ backgroundColor: sport.bg }}>
                      <Icon path={sport.icon} className="w-5 h-5" stroke={sport.color} width={1.8} />
                    </div>
                    <p className="text-[11px] font-semibold truncate w-full" style={{ color: NAVY }}>{f.facility_name}</p>
                    {avail === undefined ? (
                      <span className="text-[10px] text-gray-300">Checking…</span>
                    ) : avail ? (
                      <span className="text-[10px] font-bold" style={{ color: "#16a34a" }}>Available</span>
                    ) : (
                      <span className="text-[10px] font-bold text-gray-400">Unavailable</span>
                    )}
                  </button>
                );
              })}
            </div>
          )}
          {FEATURE_FLAGS.bookingUI && (
            <button onClick={() => setActive("book")} className="w-full text-xs font-semibold flex items-center justify-center gap-1.5 pt-2 border-t border-gray-100" style={{ color: PRIMARY }}>
              View All Facilities <Icon path={ICONS.arrowRight} className="w-3.5 h-3.5" stroke={PRIMARY} width={2.2} />
            </button>
          )}
        </div>

        {/* Recent activity */}
        <div className="bg-white rounded-2xl shadow-sm p-5">
          <div className="flex items-center gap-2 mb-1">
            <Icon path={ICONS.clock} className="w-5 h-5" stroke={PRIMARY} width={1.8} />
            <h3 className="font-bold text-sm" style={{ color: NAVY }}>Recent Activity</h3>
          </div>
          <p className="text-[11px] text-gray-400 mb-4">Your recent actions and updates</p>
          {recentActivity.length === 0 ? (
            <p className="text-xs text-gray-400 py-6 text-center">No recent activity yet.</p>
          ) : (
            <div className="space-y-3 mb-3">
              {recentActivity.map((item) => (
                <div key={item.id} className="flex items-start gap-3">
                  <div
                    className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0"
                    style={{ backgroundColor: item.type === "payment" ? "#f3ecff" : "#fff6e6" }}
                  >
                    <Icon
                      path={item.type === "payment" ? ICONS.payments : ICONS.attendance}
                      className="w-4 h-4"
                      stroke={item.type === "payment" ? "#7c3aed" : "#d97706"}
                      width={1.8}
                    />
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="text-xs font-semibold truncate" style={{ color: NAVY }}>{item.title}</p>
                    {item.detail && <p className="text-[11px] text-gray-400 truncate">{item.detail}</p>}
                  </div>
                  <span className="text-[10px] text-gray-400 flex-shrink-0 whitespace-nowrap">{timeAgo(item.date)}</span>
                </div>
              ))}
            </div>
          )}
          <button onClick={() => setActive("payments")} className="w-full text-xs font-semibold flex items-center justify-center gap-1.5 pt-2 border-t border-gray-100" style={{ color: PRIMARY }}>
            View All Activity <Icon path={ICONS.arrowRight} className="w-3.5 h-3.5" stroke={PRIMARY} width={2.2} />
          </button>
        </div>
      </div>
    </>
  );

  const FacilitiesStep = () => (
    <div className="space-y-5">
      <div className="bg-white rounded-2xl shadow-sm p-5">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <p className="text-xs font-bold uppercase tracking-wide text-gray-400 mb-1.5">Select Date</p>
            <input
              type="date"
              value={bookDate}
              min={todayStr()}
              max={maxBookDateStr()}
              onChange={(e) => setBookDate(e.target.value)}
              className="border border-gray-200 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 text-gray-700"
            />
          </div>
          <div className="flex flex-wrap gap-4 text-xs text-gray-500">
            <LegendDot color="#dcfce7" border="#16a34a" label="Available" />
            <LegendDot color="#fef3c7" border="#f59e0b" label="Full" />
            <LegendDot color="#fee2e2" border="#ef4444" label="Blocked" />
          </div>
        </div>
      </div>

      <div>
        <h3 className="font-bold text-base mb-1" style={{ color: NAVY }}>Choose a Facility</h3>
        <p className="text-xs text-gray-400 mb-4">
          Select a facility and check real-time availability. Tap "View Availability" to see time slots.
        </p>

        {facilities.length === 0 ? (
          <div className="bg-white rounded-2xl shadow-sm">
            <EmptyState icon={ICONS.facilities} text="No facilities are configured yet." />
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {facilities.map((f, i) => {
              const accent = facilityAccent(i);
              const avail = facilityAvailabilityMap[f.id];
              const preview = avail?.facility_available_for_booking ? previewSlotsFor(avail) : [];
              return (
                <div key={f.id} className="bg-white rounded-2xl shadow-sm overflow-hidden flex flex-col">
                  <div className="p-4 pb-0 flex items-center gap-2.5">
                    <div className="w-11 h-11 rounded-full flex items-center justify-center flex-shrink-0" style={{ backgroundColor: accent.color }}>
                      <Icon path={ICONS.facilities} className="w-5 h-5" stroke="#fff" width={1.8} />
                    </div>
                    <h4 className="font-bold text-sm truncate" style={{ color: NAVY }}>{f.facility_name}</h4>
                  </div>

                  {/* Facility photo placeholder — the backend doesn't return a
                      photo field yet (no photo_url/photo_path on GET
                      /api/facilities). Once it does, this ternary already
                      reads f.photo_url through storageUrl() the same way
                      member/coach photos are rendered elsewhere — no other
                      change needed here to go live. */}
                  <div
                    className="mx-4 mt-3 h-32 rounded-xl flex items-center justify-center overflow-hidden"
                    style={{ background: `linear-gradient(135deg, ${accent.bg}, #ffffff)` }}
                  >
                    {f.photo_url ? (
                      <img src={storageUrl(f.photo_url)} alt={f.facility_name} className="w-full h-full object-cover" />
                    ) : getFacilityFallbackImage(f.facility_name) ? (
                      <img src={getFacilityFallbackImage(f.facility_name)} alt={f.facility_name} className="w-full h-full object-cover" />
                    ) : (
                      <Icon path={ICONS.facilities} className="w-12 h-12" stroke={accent.color} width={1.2} />
                    )}
                  </div>

                  <div className="p-4 flex-1 flex flex-col">
                    <div className="flex items-center gap-1.5 text-xs font-semibold text-gray-500 mb-2">
                      <Icon path={ICONS.clock} className="w-3.5 h-3.5" stroke="#9ca3af" width={2} />
                      Next Available Slots
                    </div>
                    {avail === undefined ? (
                      <p className="text-xs text-gray-400 mb-3">Loading…</p>
                    ) : !avail || !avail.facility_available_for_booking ? (
                      <p className="text-xs text-gray-400 mb-3">Not available for booking.</p>
                    ) : preview.length === 0 ? (
                      <p className="text-xs text-gray-400 mb-3">No time slots configured.</p>
                    ) : (
                      <div className="grid grid-cols-2 gap-2 mb-3">
                        {preview.map((p) => (
                          <div key={p.start} className="rounded-lg border border-gray-100 px-2 py-1.5 text-center">
                            <p className="text-[11px] font-semibold text-gray-500 mb-1">{p.start}</p>
                            <span
                              className="text-[10px] font-bold px-1.5 py-0.5 rounded-full"
                              style={
                                p.availableCount > 0
                                  ? { backgroundColor: "#dcfce7", color: "#15803d" }
                                  : { backgroundColor: "#fee2e2", color: "#b91c1c" }
                              }
                            >
                              {p.availableCount} Available
                            </span>
                          </div>
                        ))}
                      </div>
                    )}

                    <div className="mt-auto pt-3 border-t border-gray-100 flex items-center justify-between gap-3">
                      <div className="flex items-center gap-1.5 text-xs text-gray-500 min-w-0">
                        <Icon path={ICONS.payments} className="w-3.5 h-3.5 flex-shrink-0" stroke="#9ca3af" width={1.8} />
                        <span className="truncate">
                          Booking Fee<br /><span className="font-bold text-gray-700">LKR {f.booking_fee ?? "—"}</span>
                        </span>
                      </div>
                      <button
                        onClick={() => openFacilityAvailability(f.id)}
                        className="flex items-center gap-1 px-3.5 py-2 rounded-lg text-xs font-semibold text-white transition flex-shrink-0"
                        style={{ backgroundColor: accent.color }}
                      >
                        View Availability
                        <Icon path={ICONS.chevron} className="w-3.5 h-3.5 -rotate-90" stroke="#fff" width={2.4} />
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      <HowItWorksCard />
    </div>
  );

  const AvailabilityStep = () => {
    const timeCols = availability?.slots?.[0]?.time_slots || [];
    // Same per-facility accent color as its card on the Choose a Facility
    // grid, keyed by that facility's position in the list.
    const accent = facilityAccent(Math.max(0, facilities.findIndex((f) => f.id === selectedFacilityId)));
    return (
      <div className="space-y-5">
        <div className="bg-white rounded-2xl shadow-sm p-5">
          <button
            onClick={goToFacilities}
            className="flex items-center gap-1 text-xs font-semibold mb-4"
            style={{ color: PRIMARY }}
          >
            <Icon path={ICONS.back} className="w-3.5 h-3.5" stroke={PRIMARY} width={2} /> Back to Facilities
          </button>

          <div className="flex flex-col lg:flex-row gap-5">
            {/* Same photo placeholder as the facility card — see the note there. */}
            <div
              className="lg:w-64 h-40 lg:h-auto rounded-xl flex items-center justify-center flex-shrink-0 overflow-hidden"
              style={{ background: `linear-gradient(135deg, ${accent.bg}, #ffffff)` }}
            >
              {selectedFacility?.photo_url ? (
                <img src={storageUrl(selectedFacility.photo_url)} alt={selectedFacility.facility_name} className="w-full h-full object-cover" />
              ) : getFacilityFallbackImage(selectedFacility?.facility_name) ? (
                <img src={getFacilityFallbackImage(selectedFacility.facility_name)} alt={selectedFacility.facility_name} className="w-full h-full object-cover" />
              ) : (
                <Icon path={ICONS.facilities} className="w-14 h-14" stroke={accent.color} width={1.2} />
              )}
            </div>

            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2.5 mb-2">
                <div className="w-11 h-11 rounded-full flex items-center justify-center flex-shrink-0" style={{ backgroundColor: accent.color }}>
                  <Icon path={ICONS.facilities} className="w-5 h-5" stroke="#fff" width={1.8} />
                </div>
                <h3 className="font-bold text-base" style={{ color: NAVY }}>{selectedFacility?.facility_name || "—"}</h3>
              </div>
              {selectedFacility?.description && <p className="text-xs text-gray-500 mb-3">{selectedFacility.description}</p>}
              {/swim|pool/i.test(selectedFacility?.facility_name || "") && (
                <p className="flex items-center gap-2 text-xs font-medium text-amber-800 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 mb-3">
                  <Icon path={ICONS.alertCircle} className="w-4 h-4 flex-shrink-0" stroke="#f59e0b" width={1.8} />
                  A maximum of 10 users can book a single lane.
                </p>
              )}
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 text-xs">
                <div className="flex items-center gap-2">
                  <div className="w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0" style={{ backgroundColor: accent.color }}>
                    <Icon path={ICONS.payments} className="w-4 h-4" stroke="#fff" width={1.8} />
                  </div>
                  <div>
                    <p className="text-gray-400">Booking Fee</p>
                    <p className="font-bold" style={{ color: NAVY }}>LKR {selectedFacility?.booking_fee ?? "—"}</p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0" style={{ backgroundColor: accent.color }}>
                    <Icon path={ICONS.clock} className="w-4 h-4" stroke="#fff" width={1.8} />
                  </div>
                  <div>
                    <p className="text-gray-400">Operating Hours</p>
                    <p className="font-bold" style={{ color: NAVY }}>
                      {selectedFacility?.open_time ? String(selectedFacility.open_time).slice(0, 5) : "—"} – {selectedFacility?.close_time ? String(selectedFacility.close_time).slice(0, 5) : "—"}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0" style={{ backgroundColor: accent.color }}>
                    <Icon path={ICONS.facilities} className="w-4 h-4" stroke="#fff" width={1.8} />
                  </div>
                  <div>
                    <p className="text-gray-400">Courts / Slots</p>
                    <p className="font-bold" style={{ color: NAVY }}>{selectedFacility?.slot_count ?? "—"}</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Booking steps, in order: 1. date → 2. court / slot → 3. time slots. */}
        <div className="bg-white rounded-2xl shadow-sm p-5">
          <StepHeading step={1} title="Select Date" subtitle={`Choose a day up to ${MEMBER_MAX_BOOKING_DAYS} days ahead.`} />
          <div className="flex flex-col sm:flex-row sm:items-center gap-5">
            <div className="sm:w-72 flex-shrink-0">
              <MiniCalendar value={bookDate} onChange={setBookDate} minDate={todayStr()} maxDate={maxBookDateStr()} />
            </div>
            <div className="flex-1 flex items-center gap-4 rounded-xl border border-blue-100 bg-blue-50 p-5">
              <div className="w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0" style={{ backgroundColor: PRIMARY }}>
                <Icon path={ICONS.calendar} className="w-6 h-6" stroke="#fff" width={1.8} />
              </div>
              <div className="min-w-0">
                <p className="text-xs font-bold uppercase tracking-wide text-gray-400">Selected Date</p>
                <p className="text-lg font-bold" style={{ color: NAVY }}>{prettyBookDate(bookDate)}</p>
                <p className="text-xs text-gray-500">Next, choose a court / slot below.</p>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-2xl shadow-sm p-5">
          <div className="mb-5">
            <StepHeading step={2} title="Select Court / Slot" subtitle={`Courts for ${prettyBookDate(bookDate)}.`} />
            <select
              value={selectedCourtSlotId ?? ""}
              onChange={(e) => {
                const val = e.target.value ? Number(e.target.value) : null;
                setSelectedCourtSlotId(val);
                setSelectedRange(null);
              }}
              disabled={availLoading || !availability?.facility_available_for_booking || timeCols.length === 0}
              className="border border-gray-200 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 text-gray-700 w-full sm:w-72 disabled:bg-gray-50 disabled:cursor-not-allowed"
            >
              <option value="">{availLoading ? "Loading courts…" : "Choose a court / slot…"}</option>
              {(availability?.slots || []).map((slot) => (
                <option key={slot.id} value={slot.id}>{prettifySlotCode(slot.slot_code)}</option>
              ))}
            </select>
          </div>

          <div className="border-t border-gray-100 pt-5">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4">
            <StepHeading
              step={3}
              title="Available Time Slots"
              subtitle={
                selectedCourtSlotId && availability
                  ? `Showing availability for ${bookDate} — ${prettifySlotCode(availability.slots.find((s) => s.id === selectedCourtSlotId)?.slot_code)}. Click a slot to select it — click an adjacent slot to extend, min 1 hour.`
                  : `Showing availability for ${bookDate}. Select a court / slot above to view its time slots.`
              }
            />
            <div className="flex flex-wrap gap-4 text-xs text-gray-500">
              <LegendDot color="#22c55e" border="#22c55e" label="Available" />
              <LegendDot color="#f97316" border="#f97316" label="Selected" />
              <LegendDot color="#3b82f6" border="#3b82f6" label="In Cart" />
              <LegendDot color="#ef4444" border="#ef4444" label="Booked" />
              <LegendDot color={NAVY} border={NAVY} label="Blocked" />
              <LegendDot color="#d1d5db" border="#d1d5db" label="Full" />
            </div>
          </div>

          {availLoading ? (
            <p className="text-sm text-gray-400 py-10 text-center">Loading availability…</p>
          ) : !availability ? (
            <EmptyState icon={ICONS.book} text="Select a date to see availability." />
          ) : !availability.facility_available_for_booking ? (
            <EmptyState icon={ICONS.clock} text="This facility is currently not available for booking." />
          ) : timeCols.length === 0 ? (
            <EmptyState icon={ICONS.clock} text="No time slots configured for this facility." />
          ) : (
            <>
              {!selectedCourtSlotId ? (
                <EmptyState icon={ICONS.facilities} text="Select a court / slot above to view its available time slots." />
              ) : (
                (() => {
                  const slot = availability.slots.find((s) => s.id === selectedCourtSlotId);
                  if (!slot) return <EmptyState icon={ICONS.facilities} text="Select a court / slot above to view its available time slots." />;
                  const rows = slot.time_slots;
                  return (
                    <div className="flex flex-wrap gap-2">
                      {timeCols.map((tcol, idx) => {
                        const ts = rows[idx];
                        const hasAvailableNeighbor = rowSelectable(rows[idx - 1], bookDate) || rowSelectable(rows[idx + 1], bookDate);
                        const isPast = isPastSlot(bookDate, ts.start_time);
                        const status = slotStatus(ts, hasAvailableNeighbor, isPast);
                        const isSelected =
                          selectedRange &&
                          selectedRange.facilitySlotId === slot.id &&
                          idx >= selectedRange.startIdx &&
                          idx <= selectedRange.endIdx;
                        // Cart entries can span multiple 30-min cells (one
                        // "Add to Booking" click adds a single entry covering
                        // the whole selected range), so a cell counts as "in
                        // cart" if any entry's [start_time, end_time) range
                        // contains it — not just an exact start_time match.
                        const cellStart = String(ts.start_time).slice(0, 5);
                        const isInCart = cart.entries.some(
                          (e) =>
                            e.facility_slot_id === slot.id &&
                            e.booking_date === bookDate &&
                            e.start_time <= cellStart &&
                            e.end_time > cellStart
                        );
                        const cellColor = isSelected ? "#f97316" : isInCart ? "#3b82f6" : SWATCH_COLORS[status.key];
                        const cellTextColor = status.key === "min60" || status.key === "past" ? "#4b5563" : "#ffffff";
                        return (
                          <button
                            key={`${slot.id}_${idx}`}
                            type="button"
                            disabled={!rowSelectable(ts, bookDate) || isInCart}
                            title={
                              isInCart
                                ? "Already added to your booking below — remove it there to change it"
                                : isSelected
                                ? "Selected — click the first or last selected slot to remove it"
                                : status.tooltip
                            }
                            onClick={() => handleCellClick(slot, idx)}
                            className="w-28 h-9 flex-shrink-0 flex items-center justify-center rounded-lg px-1 text-xs font-semibold whitespace-nowrap transition hover:brightness-95 disabled:cursor-not-allowed"
                            style={{ backgroundColor: cellColor, color: cellTextColor }}
                          >
                            {String(tcol.start_time).slice(0, 5)}–{String(tcol.end_time || "").slice(0, 5)}
                          </button>
                        );
                      })}
                    </div>
                  );
                })()
              )}
            </>
          )}
          </div>

          <p className="text-xs text-gray-400 mt-5">Note: Time slots are displayed in 24-hour format.</p>
        </div>

        {selectedRange && (
          <div className="bg-white rounded-2xl shadow-sm p-5">
            {/* Header */}
            <div className="flex items-start gap-3 mb-4">
              <div className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0" style={{ backgroundColor: PRIMARY }}>
                <Icon path={ICONS.calendar} className="w-5 h-5" stroke="#fff" width={1.8} />
              </div>
              <div>
                <h3 className="font-bold text-sm" style={{ color: NAVY }}>Add This Slot</h3>
                <p className="text-xs text-gray-400">Add it to your booking, then pick more slots or confirm below.</p>
              </div>
            </div>

            {/* Info strip */}
            <div className="flex flex-wrap items-stretch bg-white rounded-xl border border-gray-100 divide-x divide-gray-100 mb-4 overflow-hidden">
              <InfoChip icon={ICONS.facilities} label="Court / Slot" value={prettifySlotCode(selectedRange.slotCode)} />
              <InfoChip
                icon={ICONS.calendar}
                label="Date & Time"
                value={
                  <>
                    {bookDate}
                    <span className="block text-xs font-medium text-gray-500">{selectedStartTime} - {selectedEndTime}</span>
                  </>
                }
              />
              <InfoChip
                icon={ICONS.clock}
                label="Duration"
                value={
                  <span style={{ color: selectedRangeDurationMinutes < 60 ? "#b45309" : NAVY }}>
                    {formatDuration(selectedRangeDurationMinutes)}
                  </span>
                }
              />
            </div>

            {selectedRangeDurationMinutes < 60 && (
              <p className="text-xs font-medium text-amber-600 mb-4">Select at least 1 hour to book — click an adjacent slot to extend.</p>
            )}
            {cart.isFull && (
              <p className="text-xs font-medium text-amber-600 mb-4">Maximum 10 slots per booking — remove one below to add another.</p>
            )}

            {/* Actions */}
            <div className="flex justify-between gap-3">
              <button
                type="button"
                onClick={clearSelection}
                className="inline-flex items-center justify-center gap-1.5 px-8 py-2.5 rounded-xl text-sm font-semibold border border-gray-200 bg-white text-gray-600 hover:bg-gray-50 transition"
              >
                <Icon path={ICONS.x} className="w-4 h-4" width={2} />
                Cancel
              </button>
              <button
                type="button"
                onClick={handleAddToCart}
                disabled={selectedRangeDurationMinutes < 60 || cart.isFull}
                className="inline-flex items-center justify-center gap-2 px-8 py-3 rounded-xl text-sm font-bold text-white transition shadow-md hover:shadow-lg disabled:opacity-60 disabled:shadow-none"
                style={{ backgroundColor: PRIMARY, boxShadow: `0 4px 12px ${PRIMARY}4d` }}
              >
                <Icon path={ICONS.calendar} className="w-4 h-4" stroke="#fff" width={2} />
                Add to Booking
              </button>
            </div>
          </div>
        )}

        {cart.entries.length > 0 && (
          <div className="bg-white rounded-2xl shadow-sm p-5">
            <div className="flex items-start gap-3 mb-4">
              <div className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0" style={{ backgroundColor: PRIMARY }}>
                <Icon path={ICONS.book} className="w-5 h-5" stroke="#fff" width={1.8} />
              </div>
              <div>
                <h3 className="font-bold text-sm" style={{ color: NAVY }}>
                  Your Booking ({cart.entries.length}/{cart.maxEntries})
                </h3>
                <p className="text-xs text-gray-400">Review every slot below, then confirm and pay once for all of them.</p>
              </div>
            </div>

            <div className="divide-y divide-gray-100 border border-gray-100 rounded-xl overflow-hidden mb-4">
              {cart.entries.map((entry, idx) => {
                const previewEntry = cart.preview?.entries?.[idx];
                const conflict = cart.entryConflicts.get(entry.clientKey);
                return (
                  <div key={entry.clientKey} className={`p-3 flex items-center justify-between gap-3 ${conflict ? "bg-red-50" : ""}`}>
                    <div className="min-w-0">
                      <p className="text-sm font-semibold truncate" style={{ color: NAVY }}>
                        {prettifySlotCode(entry.slot_code)} — {entry.booking_date}
                      </p>
                      <p className="text-xs text-gray-500">
                        {entry.start_time} - {entry.end_time}
                        {previewEntry && <span className="ml-2 font-semibold" style={{ color: PRIMARY }}>LKR {previewEntry.entry_fee}</span>}
                      </p>
                      {conflict && (
                        <p className="text-xs font-medium text-red-600 mt-1">
                          {conflict.user_message || conflict.reason_label || "This slot is no longer available."}
                        </p>
                      )}
                    </div>
                    <button
                      type="button"
                      onClick={() => cart.removeEntry(entry.clientKey)}
                      className="flex-shrink-0 w-8 h-8 rounded-lg flex items-center justify-center text-gray-400 hover:bg-gray-100 hover:text-red-500 transition"
                      title="Remove this slot"
                    >
                      <Icon path={ICONS.x} className="w-4 h-4" width={2} />
                    </button>
                  </div>
                );
              })}
            </div>

            <div className="flex items-center justify-between mb-4 text-sm">
              <span className="text-gray-500">
                {cart.previewLoading ? "Calculating…" : cart.preview ? `${cart.preview.total_slots} slots total` : "—"}
              </span>
              <span className="font-bold text-base" style={{ color: PRIMARY }}>
                {cart.previewLoading ? "…" : cart.preview ? `LKR ${cart.preview.total_fee}` : "—"}
              </span>
            </div>

            <button
              type="button"
              onClick={() => setShowPaymentOnlyNotice(true)}
              disabled={confirmingBooking || cart.previewLoading || cart.entryConflicts.size > 0}
              className="w-full inline-flex items-center justify-center gap-2 px-8 py-3 rounded-xl text-sm font-bold text-white transition shadow-md hover:shadow-lg disabled:opacity-60 disabled:shadow-none"
              style={{ backgroundColor: PRIMARY, boxShadow: confirmingBooking ? undefined : `0 4px 12px ${PRIMARY}4d` }}
            >
              <Icon path={ICONS.lock} className="w-4 h-4" stroke="#fff" width={2} />
              {confirmingBooking ? "Locking your slots…" : "Confirm & Pay"}
            </button>
          </div>
        )}
      </div>
    );
  };

  const openBookingDetails = (id) => {
    setViewingBookingId(id);
    setViewingBooking(null);
    setViewingBookingLoading(true);
    getMyBooking(id)
      .then(setViewingBooking)
      .catch(() => toast.error("Could not load this booking's details."))
      .finally(() => setViewingBookingLoading(false));
  };

  // "Pay Now" on a Pending booking's details page — reopens the same
  // BookingPaymentModal the checkout used, continuing its original hold. Only
  // possible while that hold is live in this browser session: the backend
  // hands out a booking's payment_payload on POST /bookings alone (see
  // rememberBookingPayment in BookingPaymentModal.jsx).
  const handlePayNow = (bookingId) => {
    const resumable = getResumableBookingPayment(bookingId);
    if (!resumable) {
      toast.error(
        "This booking's payment hold has expired or was started in another browser session. Please book the time slots again."
      );
      return;
    }
    setResumingPayment(resumable);
  };

  const finishResumedPayment = () => {
    const res = resumingPayment?.response;
    forgetBookingPayment(res?.booking?.id ?? res?.booking_id);
    setResumingPayment(null);
    refreshMyBookings();
    if (viewingBookingId != null) getMyBooking(viewingBookingId).then(setViewingBooking).catch(() => {});
  };

  const MyBookingsView = () => (
    <div className="bg-white rounded-2xl shadow-sm p-5">
      <h3 className="font-bold text-sm mb-4" style={{ color: NAVY }}>My Bookings ({myBookings.length})</h3>
      {myBookingsLoading ? (
        <p className="text-sm text-gray-400 py-10 text-center">Loading your bookings…</p>
      ) : myBookings.length === 0 ? (
        <EmptyState icon={ICONS.book} text="You haven't booked a facility yet." />
      ) : (
        <div className="overflow-x-auto -mx-5 px-5">
          <table className="w-full text-left min-w-[720px]">
            <thead>
              <tr className="text-xs text-gray-400 border-b border-gray-100">
                <th className="py-2 pr-3 font-medium">Date</th>
                <th className="py-2 px-3 font-medium">Slots</th>
                <th className="py-2 px-3 font-medium">Fee</th>
                <th className="py-2 px-3 font-medium">Reference</th>
                <th className="py-2 px-3 font-medium">Status</th>
                <th className="py-2 pl-3 font-medium"></th>
              </tr>
            </thead>
            <tbody>
              {myBookings.map((b) => (
                <tr key={b.id} className="border-b border-gray-50 last:border-0">
                  <td className="py-3 pr-3 text-xs text-gray-600">
                    {fmtDate(b.booking_date)}
                    {b.total_entries > 1 && (
                      <span className="ml-1.5 text-[10px] font-bold px-1.5 py-0.5 rounded-full bg-blue-50 text-blue-700">
                        {b.total_entries} slots
                      </span>
                    )}
                  </td>
                  <td className="py-3 px-3 text-xs text-gray-600">{b.total_slots ?? "—"}</td>
                  <td className="py-3 px-3 text-xs text-gray-600 whitespace-nowrap">{money(b.total_fee ?? b.payable_amount)}</td>
                  <td className="py-3 px-3 text-xs font-mono text-gray-600">{b.payment_reference || "—"}</td>
                  <td className="py-3 px-3"><Pill>{b.payment_status}</Pill></td>
                  <td className="py-3 pl-3">
                    <button
                      type="button"
                      onClick={() => openBookingDetails(b.id)}
                      className="text-xs font-semibold whitespace-nowrap"
                      style={{ color: PRIMARY }}
                    >
                      View Details
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <p className="text-xs text-gray-400 mt-4">
        A booking's payment must be completed within 10 minutes of locking the slots — a Pending row here has likely
        already expired; please book again if so.
      </p>
    </div>
  );

  // Full-page "View Details" view, swapped in over whatever the active nav
  // tab would otherwise render (see renderView() below) — not a modal, so
  // the sidebar/header chrome stays put and "Back to Bookings" just clears
  // viewingBookingId to return to the My Bookings list.
  const BookingDetailsPage = () => {
    const copy = (value, label) => {
      if (!value) return;
      navigator.clipboard?.writeText(String(value)).then(() => toast.success(`${label} copied`));
    };
    const CopyableValue = ({ value, label }) => (
      <span className="inline-flex items-center gap-1.5">
        <span className="font-mono">{value || "—"}</span>
        {value && (
          <button
            type="button"
            onClick={() => copy(value, label)}
            className="text-gray-300 hover:text-gray-500"
            title={`Copy ${label}`}
          >
            <Icon path={ICONS.copy} className="w-3.5 h-3.5" width={1.8} />
          </button>
        )}
      </span>
    );

    if (viewingBookingLoading) {
      return (
        <div className="space-y-4">
          <BackToBookings />
          <div className="bg-white rounded-2xl shadow-sm p-10 text-center text-sm text-gray-400">Loading booking details…</div>
        </div>
      );
    }
    if (!viewingBooking) {
      return (
        <div className="space-y-4">
          <BackToBookings />
          <div className="bg-white rounded-2xl shadow-sm p-10 text-center text-sm text-gray-400">Could not load this booking.</div>
        </div>
      );
    }

    const b = viewingBooking;
    const timeRows = b.slots?.length ? b.slots : b.entries || [];
    const facilityName =
      b.slots?.[0]?.facility_name || facilities.find((f) => f.id === b.facility_id)?.facility_name || "—";
    const slotCode = b.entries?.[0]?.slot_code || b.slots?.[0]?.slot_code;

    return (
      <div className="space-y-5">
        <BackToBookings />

        <div className="bg-white rounded-2xl shadow-sm p-5 flex items-start justify-between gap-4 flex-wrap">
          <div className="flex items-center gap-3">
            <div className="w-11 h-11 rounded-xl flex items-center justify-center flex-shrink-0" style={{ backgroundColor: PRIMARY }}>
              <Icon path={ICONS.calendar} className="w-5 h-5" stroke="#fff" width={1.8} />
            </div>
            <div>
              <h2 className="font-bold text-lg" style={{ color: NAVY }}>Booking Details</h2>
              <p className="text-xs text-gray-400">View your booking information and payment details</p>
            </div>
          </div>
          <div className="flex flex-col items-end gap-1">
            <Pill>{b.booking_status}</Pill>
            <span className="text-[11px] text-gray-400">Booking ID: {b.id}</span>
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-x-4 gap-y-2 bg-white rounded-2xl shadow-sm p-5">
          <InfoChip icon={ICONS.fileText} label="Booking Reference" value={<CopyableValue value={b.payment_reference} label="Booking Reference" />} />
          <InfoChip icon={ICONS.card} label="Payment Status" value={<Pill>{b.payment_status}</Pill>} />
          <InfoChip icon={ICONS.dashboard} label="Total Slots" value={b.total_slots ?? "—"} />
          <InfoChip
            icon={ICONS.info}
            label="Payment Reference"
            value={<CopyableValue value={b.external_payment_reference || b.payment_reference} label="Payment Reference" />}
          />
          <InfoChip icon={ICONS.calendar} label="Booking Date" value={fmtDate(b.booking_date)} />
          <InfoChip icon={ICONS.clock} label="Paid At" value={fmtDateTime(b.paid_at)} />
          <InfoChip icon={ICONS.tag} label="Total Amount" value={<span style={{ color: "#16a34a" }}>{money(b.total_fee)}</span>} />
          <InfoChip icon={ICONS.check} label="Booking Status" value={<Pill>{b.booking_status}</Pill>} />
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
          <div className="bg-white rounded-2xl shadow-sm p-5">
            <h3 className="font-bold text-sm mb-3 flex items-center gap-2" style={{ color: NAVY }}>
              <Icon path={ICONS.facilities} className="w-4 h-4" stroke={PRIMARY} width={1.8} />
              Facility Information
            </h3>
            <div className="flex items-center gap-3 rounded-xl p-4" style={{ backgroundColor: "#f0fdf4" }}>
              <div className="w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0" style={{ backgroundColor: "#dcfce7" }}>
                <Icon path={ICONS.facilities} className="w-6 h-6" stroke="#16a34a" width={1.8} />
              </div>
              <div>
                <p className="font-bold text-sm" style={{ color: NAVY }}>{facilityName}</p>
                <p className="text-xs" style={{ color: "#16a34a" }}>Slot Code: {slotCode || "—"}</p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-2xl shadow-sm p-5">
            <h3 className="font-bold text-sm mb-3 flex items-center gap-2" style={{ color: NAVY }}>
              <Icon path={ICONS.fileText} className="w-4 h-4" stroke={PRIMARY} width={1.8} />
              Booking Summary
            </h3>
            <InfoRow label="Total Entries" value={b.total_entries ?? (b.entries || []).length} />
            <InfoRow label="Total Slots" value={b.total_slots} />
            <InfoRow label="Total Fee" value={money(b.total_fee)} />
            <InfoRow label="Paid Amount" value={money(b.paid_amount)} />
          </div>
        </div>

        <div className="bg-white rounded-2xl shadow-sm p-5">
          <h3 className="font-bold text-sm mb-4 flex items-center gap-2" style={{ color: NAVY }}>
            <Icon path={ICONS.clock} className="w-4 h-4" stroke={PRIMARY} width={1.8} />
            Booked Time Slots ({timeRows.length})
          </h3>
          <div className="overflow-x-auto -mx-1 px-1">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-xs text-gray-400 border-b border-gray-100">
                  <th className="py-2 pr-3 font-medium">#</th>
                  <th className="py-2 pr-3 font-medium">Slot Code</th>
                  <th className="py-2 pr-3 font-medium">Start Time</th>
                  <th className="py-2 pr-3 font-medium">End Time</th>
                  <th className="py-2 pr-3 font-medium">Status</th>
                </tr>
              </thead>
              <tbody>
                {timeRows.map((row, idx) => (
                  <tr key={row.id ?? idx} className="border-b border-gray-50 last:border-0">
                    <td className="py-2.5 pr-3 text-gray-400">{idx + 1}</td>
                    <td className="py-2.5 pr-3 font-mono text-xs text-gray-700">{row.slot_code}</td>
                    <td className="py-2.5 pr-3 text-gray-700">{formatTime12h(row.start_time)}</td>
                    <td className="py-2.5 pr-3 text-gray-700">{formatTime12h(row.end_time)}</td>
                    <td className="py-2.5 pr-3">
                      <Pill>{row.booking_status || b.booking_status}</Pill>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        <div className="rounded-2xl p-4 flex items-start gap-3" style={{ backgroundColor: LIGHT }}>
          <Icon path={ICONS.info} className="w-5 h-5 flex-shrink-0" stroke={PRIMARY} width={1.8} />
          <p className="text-xs text-gray-600">
            Thank you for using our sports facility booking system. If you have any questions or need assistance, please contact the sports
            center.
          </p>
        </div>

        {b.payment_status === "Pending" && !b.created_by_staff && (
          <div className="bg-white rounded-2xl shadow-sm p-5 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
            <div>
              <p className="font-bold text-sm" style={{ color: NAVY }}>Payment Pending</p>
              <p className="text-xs text-gray-500">Complete your card payment of {money(b.total_fee)} to confirm this booking.</p>
            </div>
            <button
              type="button"
              onClick={() => handlePayNow(b.id)}
              className="inline-flex items-center justify-center gap-2 px-6 py-2.5 rounded-xl text-sm font-bold text-white transition shadow-md hover:shadow-lg"
              style={{ backgroundColor: PRIMARY, boxShadow: `0 4px 12px ${PRIMARY}4d` }}
            >
              <Icon path={ICONS.card} className="w-4 h-4" stroke="#fff" width={2} />
              Pay Now
            </button>
          </div>
        )}
      </div>
    );
  };

  const BackToBookings = () => (
    <button
      type="button"
      onClick={() => setViewingBookingId(null)}
      className="inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-2 rounded-lg bg-white shadow-sm hover:bg-gray-50"
      style={{ color: NAVY }}
    >
      <Icon path={ICONS.back} className="w-3.5 h-3.5" width={2} />
      Back to Bookings
    </button>
  );

  const BookFacilityView = () => (
    <div className="space-y-4">
      <div className="inline-flex rounded-xl bg-gray-100 p-1 gap-1">
        {[
          { key: "book", label: "Choose Facility" },
          { key: "mine", label: "My Bookings" },
        ].map((t) => (
          <button
            key={t.key}
            type="button"
            onClick={() => setBookingView(t.key)}
            className="px-4 py-1.5 rounded-lg text-xs font-semibold transition"
            style={
              bookingView === t.key
                ? { backgroundColor: PRIMARY, color: "#fff" }
                : { color: "#6b7280" }
            }
          >
            {t.label}
          </button>
        ))}
      </div>
      {bookingView === "mine" ? <MyBookingsView /> : bookingStep === "availability" ? <AvailabilityStep /> : <FacilitiesStep />}
    </div>
  );

  const AttendanceView = () => (
    <div className="bg-white rounded-2xl shadow-sm p-5">
      <h3 className="font-bold text-sm mb-4" style={{ color: NAVY }}>My Attendance ({attendances.length})</h3>
      {attendances.length === 0 ? (
        <EmptyState icon={ICONS.attendance} text="You have no attendance records yet." />
      ) : (
        <div className="overflow-x-auto -mx-5 px-5">
          <table className="w-full text-left min-w-[600px]">
            <thead>
              <tr className="text-xs text-gray-400 border-b border-gray-100">
                <th className="py-2 pr-3 font-medium">Scanned At</th>
                <th className="py-2 px-3 font-medium">Booking Date</th>
                <th className="py-2 px-3 font-medium">Slots</th>
                <th className="py-2 px-3 font-medium">Fee</th>
                <th className="py-2 pl-3 font-medium">Code</th>
              </tr>
            </thead>
            <tbody>
              {attendances.map((a) => (
                <tr key={a.id} className="border-b border-gray-50 last:border-0">
                  <td className="py-3 pr-3 text-xs text-gray-600">{fmtDateTime(a.scanned_at)}</td>
                  <td className="py-3 px-3 text-xs text-gray-600">{fmtDate(a.booking_date)}</td>
                  <td className="py-3 px-3 text-xs text-gray-600">{a.total_slots ?? "—"}</td>
                  <td className="py-3 px-3 text-xs text-gray-600 whitespace-nowrap">{money(a.total_fee)}</td>
                  <td className="py-3 pl-3 text-xs text-gray-600 font-mono">{a.scanned_code || "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );

  // Summarizes a payment list for a card face: how many, how much paid in
  // total, and the most recent payment date (paid_at, falling back to
  // created_at for rows that haven't cleared yet).
  const summarizePayments = (list) => {
    const total = list.reduce((sum, p) => sum + (Number(p.paid_amount ?? p.amount) || 0), 0);
    const last = list.reduce((latest, p) => {
      const d = p.paid_at || p.created_at;
      return d && (!latest || d > latest) ? d : latest;
    }, null);
    return { count: list.length, total, last };
  };

  const PaymentCard = ({ icon, accent, accentBg, title, description, summary, onViewDetails }) => (
    <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5 flex flex-col">
      <div className="flex items-start justify-between mb-3">
        <div className="w-11 h-11 rounded-xl flex items-center justify-center flex-shrink-0" style={{ backgroundColor: accentBg }}>
          <Icon path={icon} className="w-5 h-5" stroke={accent} width={1.8} />
        </div>
        <span className="text-xs font-semibold px-2.5 py-1 rounded-full whitespace-nowrap" style={{ backgroundColor: accentBg, color: accent }}>
          {summary.count} Payment{summary.count === 1 ? "" : "s"}
        </span>
      </div>
      <h4 className="font-bold text-sm mb-1" style={{ color: NAVY }}>{title}</h4>
      <p className="text-xs text-gray-400 mb-4">{description}</p>
      <div className="mt-auto pt-4 border-t border-gray-100 flex items-end justify-between gap-3">
        <div>
          <p className="text-xs text-gray-400 mb-0.5">Total Paid</p>
          <p className="text-sm font-bold" style={{ color: accent }}>{money(summary.total)}</p>
        </div>
        <div>
          <p className="text-xs text-gray-400 mb-0.5">Last Payment</p>
          <p className="text-xs text-gray-600">{summary.last ? fmtDate(summary.last) : "—"}</p>
        </div>
        <button
          onClick={onViewDetails}
          className="flex items-center gap-1 text-xs font-semibold px-3 py-1.5 rounded-lg border whitespace-nowrap"
          style={{ borderColor: accent, color: accent }}
        >
          View Details
          <Icon path="M9 5l7 7-7 7" className="w-3.5 h-3.5" stroke={accent} width={2} />
        </button>
      </div>
    </div>
  );

  const PaymentDetailTable = ({ title, rows, onBack }) => (
    <div className="bg-white rounded-2xl shadow-sm p-5">
      <button onClick={onBack} className="flex items-center gap-1 text-xs font-semibold mb-4" style={{ color: PRIMARY }}>
        <Icon path={ICONS.back} className="w-3.5 h-3.5" stroke={PRIMARY} width={2} /> Back to My Payments
      </button>
      <h3 className="font-bold text-sm mb-4" style={{ color: NAVY }}>{title} ({rows.length})</h3>
      {rows.length === 0 ? (
        <EmptyState icon={ICONS.payments} text={`No ${title.toLowerCase()} recorded yet.`} />
      ) : (
        <div className="overflow-x-auto -mx-5 px-5">
          <table className="w-full text-left min-w-[640px]">
            <thead>
              <tr className="text-xs text-gray-400 border-b border-gray-100">
                <th className="py-2 pr-3 font-medium">Reference</th>
                <th className="py-2 px-3 font-medium">Payable</th>
                <th className="py-2 px-3 font-medium">Paid</th>
                <th className="py-2 px-3 font-medium">Status</th>
                <th className="py-2 pl-3 font-medium">Date</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((p) => (
                <tr key={p.id ?? p.process_id ?? p.payment_id} className="border-b border-gray-50 last:border-0">
                  <td className="py-3 pr-3 text-xs font-mono text-gray-600">{p.payment_reference || "—"}</td>
                  <td className="py-3 px-3 text-xs text-gray-600 whitespace-nowrap">{money(p.payable_amount)}</td>
                  <td className="py-3 px-3 text-xs font-semibold text-gray-700 whitespace-nowrap">{money(p.paid_amount ?? p.amount)}</td>
                  <td className="py-3 px-3"><Pill>{p.payment_status}</Pill></td>
                  <td className="py-3 pl-3 text-xs text-gray-600">{fmtDate(p.paid_at || p.created_at)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );

  const PaymentsView = () => {
    if (paymentsDetail === "booking") {
      return <PaymentDetailTable title="Booking Payments" rows={payments} onBack={() => setPaymentsDetail(null)} />;
    }
    if (paymentsDetail === "registration") {
      return <PaymentDetailTable title="Registration Payments" rows={registrationPayments} onBack={() => setPaymentsDetail(null)} />;
    }
    return (
      <div>
        <h3 className="font-bold text-sm mb-4" style={{ color: NAVY }}>
          My Payments ({allPayments.length})
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <PaymentCard
            icon={ICONS.calendar}
            accent={PRIMARY}
            accentBg={LIGHT}
            title="Booking Payments"
            description="View and manage your facility booking payments."
            summary={summarizePayments(payments)}
            onViewDetails={() => setPaymentsDetail("booking")}
          />
          <PaymentCard
            icon={ICONS.userCheck}
            accent="#15803d"
            accentBg="#dcfce7"
            title="Registration Payments"
            description="View and manage your registration fee payments."
            summary={summarizePayments(registrationPayments)}
            onViewDetails={() => setPaymentsDetail("registration")}
          />
        </div>
      </div>
    );
  };

  const ClubView = () => (
    <div className="bg-white rounded-2xl shadow-sm p-6">
      <h3 className="font-bold text-sm mb-4" style={{ color: NAVY }}>My Club</h3>
      {!member.club_id ? (
        <EmptyState icon={ICONS.club} text="You are not linked to a club yet." />
      ) : !club ? (
        <p className="text-sm text-gray-400 py-8 text-center">Loading club…</p>
      ) : (
        <>
          <div className="flex items-center gap-4 mb-5">
            <div className="w-14 h-14 rounded-2xl flex items-center justify-center" style={{ background: `linear-gradient(135deg, ${PRIMARY_DARK}, ${PRIMARY})` }}>
              <Icon path={ICONS.club} className="w-7 h-7" stroke="#fff" width={1.8} />
            </div>
            <div className="min-w-0">
              <h4 className="font-bold text-lg truncate" style={{ color: NAVY }}>{club.club_name}</h4>
              <p className="text-sm text-gray-400">{club.club_id}</p>
            </div>
            <div className="ml-auto"><Pill>{club.club_status}</Pill></div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-y-2 gap-x-6">
            <InfoRow label="Registration No" value={club.reg_no} />
            <InfoRow label="Registration Year" value={club.reg_year} />
            <InfoRow label="Primary Phone" value={club.primary_phone_number} />
            <InfoRow label="No. of Coaches" value={club.no_of_coaches} />
            <InfoRow label="Members" value={club.approved_member_count} />
            <InfoRow label="Address" value={club.address} />
          </div>
        </>
      )}
    </div>
  );

  // Lost-card reprint — submit + track (see IdCardReprintController). There
  // is no self-service payment step here: once a Director/Admin approves a
  // request, payment is only ever taken at the counter by staff (the backend
  // has no online/CyberExt path for reprints), so a "Pending payment" row
  // just shows the payable amount/reference for the member to bring to the
  // counter rather than a "Pay" button.
  const hasPendingReprint = reprintRequests.some((r) => r.approval_status === "Pending");
  const IdCardReprintView = () => (
    <div className="space-y-3">
      <div className="bg-white rounded-2xl shadow-sm p-4 sm:p-5">
        <div className="flex items-center gap-6">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-3 mb-1 flex-wrap">
              <div className="w-11 h-11 rounded-xl flex items-center justify-center flex-shrink-0" style={{ backgroundColor: LIGHT }}>
                <Icon path={ICONS.card} className="w-5 h-5" stroke={PRIMARY} width={1.8} />
              </div>
              <h3 className="font-bold text-sm" style={{ color: NAVY }}>Lost your card?</h3>
              <Pill>{member.card_status}</Pill>
            </div>
            <p className="text-xs text-gray-400 mt-2 max-w-xl">
              Submit a request to reprint your lost ID card. Submitting bumps your card version immediately — your
              current QR stops working right away — and you'll be notified once a Director/Admin has reviewed it.
            </p>

            {hasPendingReprint ? (
              <p className="text-sm text-amber-700 bg-amber-50 rounded-lg px-4 py-3 mt-3 max-w-xl">
                You already have a reprint request pending director approval — check its status below.
              </p>
            ) : (
              <form onSubmit={handleSubmitReprint} className="space-y-3 mt-3 max-w-xl">
                <div>
                  <label className="block text-xs text-gray-500 mb-1.5">Reason</label>
                  <div className="relative">
                    <textarea
                      ref={reprintReasonRef}
                      className={profileInputCls("reprint_reason")}
                      rows={3}
                      maxLength={1000}
                      placeholder="e.g. Lost ID card"
                      value={reprintReason}
                      onChange={(e) => setReprintReason(e.target.value)}
                      required
                    />
                    <span className="absolute bottom-2 right-3 text-[10px] text-gray-400">{reprintReason.length}/1000</span>
                  </div>
                </div>
                <div>
                  <label className="block text-xs text-gray-500 mb-1.5">Police report</label>
                  <div className="flex items-center gap-2 flex-wrap">
                    <label className="cursor-pointer flex-shrink-0">
                      <span className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg border border-gray-200 bg-gray-50 text-xs font-semibold text-gray-700 hover:bg-gray-100 transition">
                        <Icon path={ICONS.upload} className="w-3.5 h-3.5" width={2} />
                        Choose File
                      </span>
                      <input
                        type="file"
                        accept=".pdf,.jpg,.jpeg,.png,.webp"
                        className="hidden"
                        onChange={(e) => setReprintFile(e.target.files?.[0] ?? null)}
                        required
                      />
                    </label>
                    <span className="text-xs text-gray-400 truncate">{reprintFile ? reprintFile.name : "No file selected"}</span>
                  </div>
                  <p className="text-[11px] text-gray-400 mt-1.5">Accepted formats: PDF, JPG, PNG, WEBP · Max 5MB</p>
                </div>
                {reprintError && <p className="text-xs text-red-500">{reprintError}</p>}
                <button
                  type="submit"
                  disabled={reprintSubmitting || !reprintFile || !reprintReason.trim()}
                  className="inline-flex items-center gap-2 px-5 py-2.5 rounded-lg text-sm font-semibold text-white transition disabled:opacity-60"
                  style={{ backgroundColor: PRIMARY }}
                >
                  <Icon path={ICONS.send} className="w-4 h-4" stroke="#fff" width={2} />
                  {reprintSubmitting ? "Submitting…" : "Submit Reprint Request"}
                </button>
              </form>
            )}
          </div>

          <img
            src="/images/idcard/reprint-hero.jpg"
            alt=""
            aria-hidden="true"
            className="hidden md:block w-48 lg:w-64 xl:w-72 flex-shrink-0 object-contain"
          />
        </div>
      </div>

      <div className="bg-white rounded-2xl shadow-sm p-4 sm:p-5">
        <div className="flex items-center gap-2.5 mb-3">
          <div className="w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0" style={{ backgroundColor: LIGHT }}>
            <Icon path={ICONS.fileText} className="w-4 h-4" stroke={PRIMARY} width={1.8} />
          </div>
          <h3 className="font-bold text-sm" style={{ color: NAVY }}>My Reprint Requests</h3>
        </div>
        {reprintLoading ? (
          <p className="text-sm text-gray-400 py-6 text-center">Loading…</p>
        ) : reprintRequests.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-6 text-center">
            <img src="/images/idcard/no-requests.jpg" alt="" aria-hidden="true" className="w-24 h-24 object-contain mb-3" />
            <p className="text-sm font-bold" style={{ color: NAVY }}>No reprint requests yet</p>
            <p className="text-xs text-gray-400 mt-1">You haven't requested a card reprint.</p>
            <button
              type="button"
              onClick={() => reprintReasonRef.current?.focus()}
              className="inline-flex items-center gap-1.5 mt-3 px-4 py-2 rounded-lg border text-xs font-semibold transition hover:bg-gray-50"
              style={{ borderColor: PRIMARY, color: PRIMARY }}
            >
              Submit a new request
              <span aria-hidden="true">+</span>
            </button>
          </div>
        ) : (
          <div className="space-y-3">
            {reprintRequests.map((r) => (
              <div key={r.id} className="border border-gray-100 rounded-xl p-3 sm:p-4">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <p className="text-sm font-semibold truncate" style={{ color: NAVY }}>{r.reason}</p>
                    <p className="text-xs text-gray-400">{fmtDateTime(r.created_at)}</p>
                  </div>
                  <Pill>{r.approval_status}</Pill>
                </div>
                {r.approval_status === "Rejected" && r.rejection_note && (
                  <p className="text-xs text-red-600 mt-2">Rejection reason: {r.rejection_note}</p>
                )}
                {r.approval_status === "Approved" && r.payment_status !== "Paid" && (
                  <div className="text-xs text-amber-700 bg-amber-50 rounded-lg px-3 py-2 mt-2 space-y-1.5">
                    <p>Approved — please pay {money(r.payable_amount)} to continue.</p>
                    {r.payment_reference && (
                      <p>
                        Reference: <span className="font-mono font-semibold">{r.payment_reference}</span>
                      </p>
                    )}
                    {r.can_pay && (
                      <button
                        type="button"
                        onClick={() => setPayingReprint(r)}
                        className="px-4 py-1.5 rounded-lg text-xs font-semibold text-white transition"
                        style={{ backgroundColor: PRIMARY }}
                      >
                        Pay Now
                      </button>
                    )}
                  </div>
                )}
                {r.payment_status === "Paid" && (
                  <p className="text-xs text-emerald-700 mt-2">Paid on {fmtDate(r.paid_at)} — your new card is queued for printing.</p>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );

  // Membership renewal — request + track (see MembershipRenewalController).
  // Like the reprint flow above, there is no self-service online payment
  // step: the backend never builds a CyberExt payload for a renewal, so a
  // pending renewal just shows the payable amount/reference and a voucher
  // download for the member to bring to the counter, rather than a "Pay"
  // button. Requesting again while one is already pending is safe — the
  // backend returns the existing pending renewal instead of erroring.
  // Membership progress — purely a client-side computation over the same
  // member.membership_start_date/membership_end_date already loaded for the
  // Member Information card; no new backend call. Green while there's
  // comfortable runway, orange inside the last 14 days, red once expired
  // (either the end date has passed or member_status itself says Expired).
  const MemberMembershipProgressCard = () => {
    const startStr = member?.membership_start_date;
    const endStr = member?.membership_end_date;
    if (!startStr || !endStr) return null;

    const start = new Date(startStr);
    const end = new Date(endStr);
    const now = new Date();
    if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) return null;

    const totalMs = end - start;
    const percent = totalMs > 0 ? Math.min(100, Math.max(0, ((now - start) / totalMs) * 100)) : 100;
    const daysRemaining = Math.ceil((end - now) / (1000 * 60 * 60 * 24));
    const isExpired = daysRemaining <= 0 || String(member?.member_status).toLowerCase() === "expired";
    const isApproaching = !isExpired && daysRemaining <= 14;

    const tone = isExpired
      ? { bar: "#dc2626", chip: "#fee2e2", text: "#b91c1c", track: "#fee2e2" }
      : isApproaching
      ? { bar: "#f59e0b", chip: "#fef3c7", text: "#b45309", track: "#fef3c7" }
      : { bar: "#16a34a", chip: "#dcfce7", text: "#15803d", track: "#dcfce7" };

    return (
      <div className="bg-white rounded-2xl shadow-sm p-6">
        <div className="flex flex-wrap items-center justify-between gap-2 mb-1">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0" style={{ backgroundColor: LIGHT }}>
              <Icon path={ICONS.calendar} className="w-5 h-5" stroke={PRIMARY} width={1.8} />
            </div>
            <h3 className="font-bold text-sm" style={{ color: NAVY }}>Membership Progress</h3>
          </div>
          <span
            className="text-xs font-semibold px-2.5 py-1 rounded-full whitespace-nowrap"
            style={{ backgroundColor: tone.chip, color: tone.text }}
          >
            ● {isExpired ? "Expired" : member?.member_status || "Active"}
          </span>
        </div>

        <div className="flex flex-wrap items-center justify-between gap-2 text-xs text-gray-400 mt-4 mb-2">
          <span>Started: {fmtDate(startStr)}</span>
          <span>Expires: {fmtDate(endStr)}</span>
        </div>

        <div className="w-full h-3 rounded-full overflow-hidden bg-gray-100">
          <div
            className="h-full rounded-full transition-all"
            style={{ width: `${percent}%`, backgroundColor: tone.bar }}
          />
        </div>

        <div className="flex flex-wrap items-center justify-between gap-2 mt-3">
          <p className="text-xs flex items-center gap-1.5" style={{ color: tone.text }}>
            <Icon path={ICONS.check} className="w-3.5 h-3.5" stroke={tone.text} width={2} />
            {isExpired ? "Membership period has ended" : `${percent.toFixed(0)}% of membership period completed`}
          </p>
          <p className="text-xs font-semibold flex items-center gap-1.5" style={{ color: tone.text }}>
            <Icon path={ICONS.clock} className="w-3.5 h-3.5" stroke={tone.text} width={2} />
            {isExpired
              ? `Expired ${Math.abs(daysRemaining)} day${Math.abs(daysRemaining) === 1 ? "" : "s"} ago`
              : `${daysRemaining} day${daysRemaining === 1 ? "" : "s"} remaining until renewal`}
          </p>
        </div>
      </div>
    );
  };

  const RenewalView = () => {
    const renewalWindowOpen = isRenewalWindowOpen(member.membership_end_date);
    return (
    <div className="space-y-5">
      <MemberMembershipProgressCard />

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5 items-start">
      <div className="bg-white rounded-2xl shadow-sm p-6">
        <div className="flex items-center gap-3 mb-1">
          <div className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0" style={{ backgroundColor: LIGHT }}>
            <Icon path={ICONS.refresh} className="w-5 h-5" stroke={PRIMARY} width={1.8} />
          </div>
          <div>
            <h3 className="font-bold text-sm" style={{ color: NAVY }}>Renew your membership</h3>
            <p className="text-xs text-gray-400">
              Current status: <Pill>{member.member_status}</Pill> · Expires {fmtDate(member.membership_end_date)}
            </p>
          </div>
        </div>
        <p className="text-xs text-gray-400 mt-4 mb-4">
          Requesting creates (or reuses) a pending renewal for one additional year from your current — or today's, if
          already expired — membership end date. Payment is taken at the counter; you'll get a reference and a
          printable voucher below once requested.
        </p>
        <button
          onClick={handleRequestRenewal}
          disabled={requestingRenewal || !renewalWindowOpen}
          className="px-5 py-2.5 rounded-lg text-sm font-semibold text-white transition disabled:opacity-60"
          style={{ backgroundColor: PRIMARY }}
        >
          {requestingRenewal ? "Requesting…" : "Request Renewal"}
        </button>
        {!renewalWindowOpen && (
          <p className="text-xs text-amber-600 mt-2">
            Renewal requests open on {fmtDate(renewalWindowStartDate(member.membership_end_date))} — {RENEWAL_WINDOW_DAYS} days before your expiry date.
          </p>
        )}
      </div>

      <div className="bg-white rounded-2xl shadow-sm p-6">
        <h3 className="font-bold text-sm mb-4" style={{ color: NAVY }}>My Renewals</h3>
        {renewalsLoading ? (
          <p className="text-sm text-gray-400 py-8 text-center">Loading…</p>
        ) : renewals.length === 0 ? (
          <EmptyState icon={ICONS.refresh} text="You haven't requested a membership renewal." />
        ) : (
          <div className="space-y-3">
            {renewals.map((r) => {
              const paid = Boolean(r.paid_at);
              return (
                <div key={r.renewal_id} className="border border-gray-100 rounded-xl p-4">
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <p className="text-sm font-semibold truncate" style={{ color: NAVY }}>
                        New end date: {fmtDate(r.new_membership_end_date)}
                      </p>
                      <p className="text-xs text-gray-400">{fmtDateTime(r.created_at)}</p>
                    </div>
                    <Pill>{paid ? "Paid" : "Pending"}</Pill>
                  </div>
                  {!paid ? (
                    <div className="text-xs text-amber-700 bg-amber-50 rounded-lg px-3 py-2 mt-2 space-y-1">
                      <p>Pay {money(r.payable_amount)} by card or at the counter to activate this renewal.</p>
                      {r.payment_reference && (
                        <p>
                          Reference: <span className="font-mono font-semibold">{r.payment_reference}</span>
                        </p>
                      )}
                      <div className="flex items-center gap-3 flex-wrap mt-1">
                        <button
                          type="button"
                          onClick={() => setPayingRenewal(r)}
                          className="inline-flex items-center gap-1.5 text-xs font-semibold"
                          style={{ color: PRIMARY }}
                        >
                          <Icon path={ICONS.card} className="w-3.5 h-3.5" stroke={PRIMARY} width={2} />
                          Pay Now
                        </button>
                        <button
                          type="button"
                          onClick={() => handleDownloadRenewalVoucher(r.renewal_id)}
                          disabled={downloadingRenewalVoucher === r.renewal_id}
                          className="inline-flex items-center gap-1.5 text-xs font-semibold"
                          style={{ color: PRIMARY }}
                        >
                          <Icon path={ICONS.download} className="w-3.5 h-3.5" stroke={PRIMARY} width={2} />
                          {downloadingRenewalVoucher === r.renewal_id ? "Opening…" : "Download voucher"}
                        </button>
                      </div>
                    </div>
                  ) : (
                    <p className="text-xs text-emerald-700 mt-2">
                      Paid on {fmtDate(r.paid_at)} — membership extended to {fmtDate(r.new_membership_end_date)}.
                    </p>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
      </div>
    </div>
    );
  };

  const profileInputCls = (name) =>
    `w-full rounded-lg px-3 py-2 text-sm text-gray-700 focus:outline-none focus:ring-2 transition ${
      profileErrors[name]
        ? "border border-red-400 focus:ring-red-400"
        : "border border-gray-200 focus:ring-blue-500"
    }`;

  const setProfileField = (name) => (e) => {
    const { value } = e.target;
    setProfileForm((f) => ({ ...f, [name]: value }));
    setProfileErrors((x) => (x[name] ? { ...x, [name]: "" } : x));
  };

  const ProfileView = () => (
    <div className="bg-white rounded-2xl shadow-sm p-6 max-w-2xl">
      <div className="flex items-center gap-4 mb-6">
        <Avatar name={memberName} url={photoUrl} size={72} />
        <div className="min-w-0">
          <h3 className="font-bold text-lg" style={{ color: NAVY }}>{memberFullName}</h3>
          <p className="text-sm text-gray-400">{member.member_type || (isClub ? "Club Student" : "Independent")}</p>
        </div>
        {!editingProfile && (
          <button
            onClick={startEditProfile}
            className="ml-auto flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-semibold flex-shrink-0"
            style={{ backgroundColor: LIGHT, color: PRIMARY }}>
            <Icon path={ICONS.edit} className="w-4 h-4" stroke={PRIMARY} width={1.8} /> Edit Details
          </button>
        )}
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-y-3 gap-x-6">
        <InfoRow label="Member ID" value={member.member_code || member.id} />
        <InfoRow label="Email" value={member.email} />
        <InfoRow label="Gender" value={member.member_gender} />
        <InfoRow label="NIC / Student ID" value={member.nic_number} />
        <InfoRow label="Date of Birth" value={fmtDate(member.date_of_birth)} />
        {isClub && <InfoRow label="Club" value={member.club_name || member.requested_club_name} />}
        <InfoRow label="Membership Status" value={<Pill>{member.member_status}</Pill>} />
        <InfoRow label="Payment Status" value={<Pill>{member.payment_status}</Pill>} />
        <InfoRow label="ID Card Status" value={<Pill>{member.card_status}</Pill>} />
      </div>

      {/* Contact details — editable by the member */}
      <div className="mt-6 pt-5 border-t border-gray-100">
        <h4 className="font-bold text-sm mb-4" style={{ color: NAVY }}>Contact Details</h4>
        {editingProfile ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-xs text-gray-500 mb-1.5">Primary Phone</label>
              <input
                type="tel"
                inputMode="tel"
                value={profileForm.primary_phone}
                onChange={setProfileField("primary_phone")}
                placeholder="071 234 5678"
                className={profileInputCls("primary_phone")}
              />
              {profileErrors.primary_phone && <p className="text-xs text-red-500 mt-1">{profileErrors.primary_phone}</p>}
            </div>
            <div>
              <label className="block text-xs text-gray-500 mb-1.5">Secondary Phone</label>
              <input
                type="tel"
                inputMode="tel"
                value={profileForm.secondary_phone}
                onChange={setProfileField("secondary_phone")}
                placeholder="077 234 5678"
                className={profileInputCls("secondary_phone")}
              />
              {profileErrors.secondary_phone && <p className="text-xs text-red-500 mt-1">{profileErrors.secondary_phone}</p>}
            </div>
            <div className="sm:col-span-2">
              <label className="block text-xs text-gray-500 mb-1.5">Address</label>
              <input
                value={profileForm.address}
                onChange={setProfileField("address")}
                placeholder="No. 1, Main Street, Colombo"
                className={profileInputCls("address")}
              />
              {profileErrors.address && <p className="text-xs text-red-500 mt-1">{profileErrors.address}</p>}
            </div>
            <div className="sm:col-span-2 flex items-center gap-3 mt-1">
              <button
                onClick={saveProfile}
                disabled={savingProfile}
                className="inline-flex items-center justify-center gap-2 px-5 py-2.5 rounded-lg text-sm font-semibold text-white transition disabled:opacity-60"
                style={{ backgroundColor: PRIMARY }}>
                {savingProfile ? "Saving…" : "Save Changes"}
              </button>
              <button
                onClick={cancelEditProfile}
                disabled={savingProfile}
                className="px-5 py-2.5 rounded-lg text-sm font-semibold border border-gray-300 text-gray-600 hover:bg-gray-50 transition disabled:opacity-60">
                Cancel
              </button>
            </div>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-y-3 gap-x-6">
            <InfoRow label="Primary Phone" value={member.primary_phone_number} />
            <InfoRow label="Secondary Phone" value={member.secondary_phone_number} />
            <InfoRow label="Address" value={member.address} />
          </div>
        )}
      </div>
    </div>
  );

  const renderView = () => {
    if (loading) return (
      <div className="flex flex-col items-center justify-center py-20 gap-3">
        <svg className="animate-spin w-8 h-8 text-blue-600" fill="none" viewBox="0 0 24 24">
          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z" />
        </svg>
        <p className="text-sm text-gray-400">Loading your dashboard…</p>
      </div>
    );
    // "View Details" replaces whatever the active nav tab would show with a
    // full Booking Details page (not a modal) — this takes priority over the
    // switch below regardless of which nav tab is active.
    if (viewingBookingId != null) return BookingDetailsPage();
    // Same "swap in, don't navigate away" pattern as BookingDetailsPage above
    // — payingRenewal replaces the Renewal tab's own content with the
    // payment-method sub-page while "renewal" stays the active nav item.
    if (payingRenewal) {
      return (
        <MembershipRenewalPaymentModal
          renewal={payingRenewal}
          member={member}
          onClose={() => setPayingRenewal(null)}
          onPaid={() => {
            // Deliberately doesn't clear payingRenewal here — that would
            // unmount MembershipRenewalPaymentModal before it can show its
            // own "Payment Successful" state (see that component). The
            // renewals list/member profile refresh happens now regardless,
            // so the data is already current by the time the member clicks
            // Done.
            fetchRenewals();
            // Same enrich-and-merge pattern used elsewhere in this file (see
            // the /member/me effect above) — refreshes member_status/
            // membership_start_date/membership_end_date now that the
            // renewal has activated them.
            getMemberMe()
              .then((full) => {
                if (!full) return;
                setMember((prev) => {
                  const merged = { ...prev, ...full };
                  sessionStorage.setItem(STORAGE_KEY, JSON.stringify(merged));
                  return merged;
                });
              })
              .catch(() => {});
          }}
        />
      );
    }
    // Facility booking pulled temporarily via FEATURE_FLAGS.facilityBooking —
    // frontend-only, no backend/permission change, flip the flag back to
    // re-enable. Checked ahead of the membership-expiry guard below since
    // it's an independent reason for the same tab being unavailable.
    if (!FEATURE_FLAGS.facilityBooking && active === "book") {
      return (
        <div className="bg-white rounded-2xl shadow-sm p-8 text-center max-w-lg mx-auto">
          <div className="w-14 h-14 rounded-full mx-auto mb-4 flex items-center justify-center" style={{ backgroundColor: "#fef3c7" }}>
            <Icon path={ICONS.lock} className="w-6 h-6" stroke="#b45309" width={1.8} />
          </div>
          <h3 className="font-bold text-base mb-1" style={{ color: NAVY }}>Facility Booking Temporarily Unavailable</h3>
          <p className="text-sm text-gray-500">This feature has been temporarily disabled. Please check back later.</p>
        </div>
      );
    }
    // Backend-enforced too (booking/reprint requests 4xx for an expired
    // membership) — this just stops the member from getting partway through
    // either flow before finding out. Renewal is exempt from LOCKED_WHEN_EXPIRED
    // above, so it still falls through to the switch normally.
    if (membershipExpired && LOCKED_WHEN_EXPIRED.includes(active)) {
      return (
        <div className="bg-white rounded-2xl shadow-sm p-8 text-center max-w-lg mx-auto">
          <div className="w-14 h-14 rounded-full mx-auto mb-4 flex items-center justify-center" style={{ backgroundColor: "#fee2e2" }}>
            <Icon path={ICONS.lock} className="w-6 h-6" stroke="#dc2626" width={1.8} />
          </div>
          <h3 className="font-bold text-base mb-1" style={{ color: NAVY }}>Membership Expired</h3>
          <p className="text-sm text-gray-500 mb-5">
            {VIEW_TITLES[active]} is unavailable while your membership is expired. Renew your membership to regain access.
          </p>
          <button
            onClick={() => setActive("renewal")}
            className="px-5 py-2.5 rounded-lg text-sm font-semibold text-white transition"
            style={{ backgroundColor: PRIMARY }}>
            Renew Membership
          </button>
        </div>
      );
    }
    // Invoked as plain functions (not <X />) so their JSX is inlined into this
    // component's tree — editable text inputs (Profile) then keep focus across
    // re-renders instead of remounting on every keystroke.
    switch (active) {
      case "book": return FEATURE_FLAGS.bookingUI ? BookFacilityView() : DashboardView();
      case "attendance": return AttendanceView();
      case "payments": return PaymentsView();
      case "club": return ClubView();
      case "idcard": return IdCardReprintView();
      case "renewal": return RenewalView();
      case "profile": return ProfileView();
      default: return DashboardView();
    }
  };

  return (
    <div className="min-h-screen flex overflow-x-hidden bg-gradient-to-br from-blue-50 via-white to-blue-100 m-0" style={{ fontFamily: "Inter, system-ui, sans-serif" }}>
      {/* ══ SIDEBAR ══ */}
      <aside
        className={`fixed lg:static z-40 inset-y-0 left-0 w-64 flex flex-col transition-transform ${sidebarOpen ? "translate-x-0" : "-translate-x-full lg:translate-x-0"}`}
        style={{ background: `linear-gradient(180deg, ${SIDEBAR_TOP} 0%, ${SIDEBAR_BOTTOM} 100%)` }}>
        <div className="flex items-center gap-3 px-5 h-16 border-b border-white/10">
          <div className="w-9 h-9 rounded-full flex items-center justify-center bg-white">
            <img src={logo} alt="USJ" className="w-7 h-7 object-contain" />
          </div>
          <div className="leading-tight">
            <p className="text-white font-bold text-sm">USJ SPORTS</p>
            <p className="text-[10px] text-blue-200">{portalLabel}</p>
          </div>
        </div>

        <nav className="flex-1 px-3 py-4 space-y-1 overflow-y-auto">
          {NAV.map((n) => {
            const on = active === n.key;
            return (
              <button
                key={n.key}
                onClick={() => {
                  if (n.key === "book" && !FEATURE_FLAGS.facilityBooking) {
                    toast.error("Facility booking is temporarily unavailable.");
                    return;
                  }
                  if (n.locked) {
                    toast.error("Your membership has expired — renew to unlock this.");
                    return;
                  }
                  setActive(n.key);
                  setSidebarOpen(false);
                }}
                className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium transition-colors"
                style={{
                  backgroundColor: on ? PRIMARY : "transparent",
                  color: n.locked ? "rgba(255,255,255,0.35)" : on ? "#fff" : "rgba(255,255,255,0.75)",
                  fontWeight: on ? 700 : 500,
                  cursor: n.locked ? "not-allowed" : "pointer",
                }}
                title={n.locked ? "Renew your membership to unlock" : undefined}>
                <Icon path={n.icon} className="w-5 h-5" width={1.8} />
                <span className="flex-1 text-left">{n.label}</span>
                {n.locked && <Icon path={ICONS.lock} className="w-4 h-4 flex-shrink-0" width={1.8} />}
              </button>
            );
          })}
        </nav>

        <div className="px-3 py-4 border-t border-white/10">
          <button
            onClick={handleLogout}
            className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-semibold transition-colors"
            style={{ backgroundColor: "rgba(239,68,68,0.15)", color: "#fca5a5" }}
            onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = "#ef4444"; e.currentTarget.style.color = "#fff"; }}
            onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = "rgba(239,68,68,0.15)"; e.currentTarget.style.color = "#fca5a5"; }}>
            <Icon path={ICONS.logout} className="w-5 h-5" width={1.8} />
            Logout
          </button>
        </div>
      </aside>

      {sidebarOpen && <div className="fixed inset-0 bg-black/40 z-30 lg:hidden" onClick={() => setSidebarOpen(false)} />}

      {/* ══ MAIN ══ */}
      <div className="flex-1 min-w-0 flex flex-col">
        <header className="bg-white h-16 flex items-center gap-3 px-4 sm:px-6 shadow-sm sticky top-0 z-20">
          <button className="lg:hidden text-gray-600 flex-shrink-0" onClick={() => setSidebarOpen(true)} aria-label="Open menu">
            <Icon path="M4 6h16M4 12h16M4 18h16" stroke={NAVY} width={2} />
          </button>

          <div className="flex items-center gap-2 sm:gap-3 flex-shrink-0 ml-auto">
            <button
              onClick={handleRefresh}
              disabled={refreshing}
              className="p-2 rounded-full hover:bg-gray-100 transition disabled:opacity-60"
              aria-label="Refresh"
              title="Refresh dashboard">
              <Icon path={ICONS.refresh} className={`w-5 h-5 ${refreshing ? "animate-spin" : ""}`} stroke="#6b7280" width={1.8} />
            </button>
            <button className="relative text-gray-500 p-2 rounded-full hover:bg-gray-100 transition" aria-label="Notifications">
              <Icon path={ICONS.bell} className="w-5 h-5" stroke="#6b7280" width={1.8} />
              <span className="absolute top-1.5 right-1.5 w-2 h-2 rounded-full bg-red-500 ring-2 ring-white" />
            </button>
            <div className="hidden sm:flex items-center gap-2 cursor-pointer pl-1" title={memberFullName}>
              <Avatar name={memberName} url={photoUrl} size={36} />
              <div className="leading-tight">
                <p className="text-sm font-semibold truncate max-w-[9rem]" style={{ color: NAVY }}>{memberFullName}</p>
                <p className="text-[11px] text-gray-400 truncate max-w-[9rem]">{member.member_type || (isClub ? "Club Student" : "Independent")}</p>
              </div>
              <Icon path={ICONS.chevron} className="w-4 h-4 flex-shrink-0" stroke="#9ca3af" width={2} />
            </div>
            <div className="sm:hidden">
              <Avatar name={memberName} url={photoUrl} size={32} />
            </div>
          </div>
        </header>

        <main className="flex-1 px-4 sm:px-8 lg:px-12 py-6 sm:py-8 overflow-y-auto">
          {active !== "dashboard" && (
            <h1 className="font-bold text-lg sm:text-xl mb-6" style={{ color: NAVY }}>{VIEW_TITLES[active]}</h1>
          )}
          {renderView()}
        </main>
      </div>

      {payingReprint && (
        <IdCardReprintPaymentModal
          request={payingReprint}
          onClose={() => setPayingReprint(null)}
          onPaid={() => {
            toast.success("Payment confirmed — your card is queued for printing.");
            setPayingReprint(null);
            fetchReprintRequests();
          }}
        />
      )}

      {payingBooking && (
        <BookingPaymentModal
          booking={payingBooking}
          onClose={() => setPayingBooking(null)}
          onPaid={handleBookingPaid}
          onExpired={handleBookingExpired}
        />
      )}

      {resumingPayment && (
        <BookingPaymentModal
          booking={resumingPayment.response}
          lockDeadline={resumingPayment.lockDeadline}
          onClose={() => setResumingPayment(null)}
          onPaid={() => {
            toast.success("Booking paid and confirmed!");
            finishResumedPayment();
          }}
          onExpired={() => {
            toast.error("The payment hold for this booking has expired.");
            finishResumedPayment();
          }}
        />
      )}

      {showPaymentOnlyNotice && (
        <OnlinePaymentOnlyModal
          onCancel={() => setShowPaymentOnlyNotice(false)}
          onConfirm={() => {
            setShowPaymentOnlyNotice(false);
            handleConfirmBooking();
          }}
        />
      )}

      {showBookingTimeLimitNotice && (
        <BookingTimeLimitModal onContinue={() => setShowBookingTimeLimitNotice(false)} />
      )}
    </div>
  );
}

// ── Small presentational helpers ──
function InfoRow({ label, value }) {
  return (
    <div className="flex items-center justify-between py-1.5 border-b border-gray-50 last:border-0 gap-3">
      <span className="text-xs text-gray-400 flex-shrink-0">{label}</span>
      <span className="text-xs font-medium text-gray-700 text-right break-words">{value === 0 ? 0 : value || "—"}</span>
    </div>
  );
}

function EmptyState({ icon, text }) {
  return (
    <div className="flex flex-col items-center justify-center py-12 text-center">
      <div className="w-14 h-14 rounded-full flex items-center justify-center mb-3" style={{ backgroundColor: LIGHT }}>
        <Icon path={icon} className="w-7 h-7" stroke={PRIMARY} width={1.6} />
      </div>
      <p className="text-sm text-gray-400">{text}</p>
    </div>
  );
}

// One segment of the "Confirm Your Slot" info strip — small icon badge +
// stacked label/value. Used for Court/Slot, Date & Time, Duration, Lighting,
// Total Price.
function InfoChip({ icon, label, value }) {
  return (
    <div className="flex items-center gap-2.5 px-4 py-3 flex-1 min-w-[150px]">
      <div className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0" style={{ backgroundColor: LIGHT }}>
        <Icon path={icon} className="w-4 h-4" stroke={PRIMARY} width={1.8} />
      </div>
      <div className="min-w-0">
        <p className="text-[11px] text-gray-400 whitespace-nowrap">{label}</p>
        <div className="text-sm font-semibold whitespace-nowrap" style={{ color: NAVY }}>
          {value}
        </div>
      </div>
    </div>
  );
}

// "YYYY-MM-DD" → "Wednesday, 30 September 2026" (parsed as a local date, not UTC).
function prettyBookDate(ymd) {
  if (!ymd) return "—";
  const d = new Date(`${ymd}T00:00:00`);
  return Number.isNaN(d.getTime())
    ? ymd
    : d.toLocaleDateString("en-GB", { weekday: "long", day: "numeric", month: "long", year: "numeric" });
}

// Numbered heading for the booking flow's Select Date → Court / Slot → Time Slots steps.
function StepHeading({ step, title, subtitle }) {
  return (
    <div className="flex items-start gap-3 mb-3">
      <span
        className="w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0 text-xs font-bold text-white"
        style={{ backgroundColor: PRIMARY }}
      >
        {step}
      </span>
      <div className="min-w-0">
        <h3 className="font-bold text-sm" style={{ color: NAVY }}>{title}</h3>
        {subtitle && <p className="text-xs text-gray-400">{subtitle}</p>}
      </div>
    </div>
  );
}

function LegendDot({ color, border, label }) {
  return (
    <span className="inline-flex items-center gap-1.5">
      <span className="w-3.5 h-3.5 rounded border" style={{ backgroundColor: color, borderColor: border }} />
      {label}
    </span>
  );
}

// "How it works" explainer card on the Book Facility tab's Choose a
// Facility step — purely presentational (3 steps + a decorative calendar/
// clock/check illustration built from divs and the existing Icon set, no
// image asset).
function HowItWorksCard() {
  const steps = [
    { n: 1, t: "Select", d: "Choose a facility you want to book" },
    { n: 2, t: "Choose Time", d: "Pick an available time slot" },
    { n: 3, t: "Confirm & Book", d: "Review details and confirm your booking" },
  ];
  return (
    <div className="bg-white rounded-2xl shadow-sm p-5">
      <div className="flex items-center gap-2 mb-4">
        <span className="w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0" style={{ backgroundColor: PRIMARY }}>
          <Icon path={ICONS.info} className="w-3.5 h-3.5" stroke="#fff" width={2} />
        </span>
        <h4 className="font-bold text-sm" style={{ color: NAVY }}>How it works</h4>
      </div>

      <div className="flex flex-wrap md:flex-nowrap items-center gap-x-5 gap-y-4">
        {steps.map((s, idx) => (
          <React.Fragment key={s.n}>
            <div className="flex items-start gap-2.5">
              <span
                className="w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0"
                style={{ backgroundColor: LIGHT, color: PRIMARY }}
              >
                {s.n}
              </span>
              <div className="min-w-0">
                <p className="text-xs font-bold whitespace-nowrap" style={{ color: NAVY }}>{s.t}</p>
                <p className="text-[11px] text-gray-400 leading-snug whitespace-nowrap">{s.d}</p>
              </div>
            </div>
            {idx < steps.length - 1 && (
              <Icon path={ICONS.arrowRight} className="w-4 h-4 flex-shrink-0 hidden sm:block" stroke="#d1d5db" width={2} />
            )}
          </React.Fragment>
        ))}

        {/* Decorative calendar/clock/check illustration — purely visual, no
            data. Pinned to the row's far right with ml-auto instead of
            stretching the steps block to fill the row. */}
        <div className="relative w-24 h-20 flex-shrink-0 hidden lg:block ml-auto">
          <div className="absolute inset-x-0 top-2 bottom-0 rounded-xl bg-white border border-gray-100 shadow-sm overflow-hidden">
            <div className="h-5 relative" style={{ backgroundColor: PRIMARY }}>
              {[10, 34, 58, 82].map((left) => (
                <span key={left} className="absolute -top-1 w-1 h-3 rounded-full" style={{ left: `${left}%`, backgroundColor: PRIMARY_DARK }} />
              ))}
            </div>
            <div className="grid grid-cols-4 gap-1.5 p-2.5">
              {Array.from({ length: 8 }).map((_, i) => (
                <span key={i} className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: LIGHT }} />
              ))}
            </div>
          </div>
          <div className="absolute -bottom-1 -left-1 w-8 h-8 rounded-full bg-white border border-gray-100 shadow flex items-center justify-center">
            <Icon path={ICONS.clock} className="w-4 h-4" stroke={PRIMARY} width={2} />
          </div>
          <div className="absolute -bottom-1 -right-1 w-7 h-7 rounded-full flex items-center justify-center" style={{ backgroundColor: "#16a34a" }}>
            <Icon path={ICONS.check} className="w-3.5 h-3.5" stroke="#fff" width={3} />
          </div>
        </div>
      </div>
    </div>
  );
}

// Self-contained month calendar (no external date library) used by the Book
// Facility tab's "View Availability" step. `value`/`onChange` carry a
// "YYYY-MM-DD" string; `minDate`/`maxDate` (same format) disable days outside
// that range.
function MiniCalendar({ value, onChange, minDate, maxDate }) {
  const parsed = value ? new Date(`${value}T00:00:00`) : new Date();
  const [viewYear, setViewYear] = useState(parsed.getFullYear());
  const [viewMonth, setViewMonth] = useState(parsed.getMonth());

  // Jump the visible month back to the selected date if it changes from
  // outside this component (e.g. cleared, or set via the date input).
  useEffect(() => {
    if (!value) return;
    const d = new Date(`${value}T00:00:00`);
    if (Number.isNaN(d.getTime())) return;
    setViewYear(d.getFullYear());
    setViewMonth(d.getMonth());
  }, [value]);

  const monthLabel = new Date(viewYear, viewMonth, 1).toLocaleDateString("en-US", { month: "long", year: "numeric" });
  const leadDays = (new Date(viewYear, viewMonth, 1).getDay() + 6) % 7; // Monday-first
  const daysInMonth = new Date(viewYear, viewMonth + 1, 0).getDate();
  const cells = [...Array(leadDays).fill(null), ...Array.from({ length: daysInMonth }, (_, i) => i + 1)];
  const toISO = (day) => `${viewYear}-${String(viewMonth + 1).padStart(2, "0")}-${String(day).padStart(2, "0")}`;

  const goMonth = (delta) => {
    const d = new Date(viewYear, viewMonth + delta, 1);
    setViewYear(d.getFullYear());
    setViewMonth(d.getMonth());
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-3">
        <button type="button" onClick={() => goMonth(-1)} aria-label="Previous month" className="w-7 h-7 flex items-center justify-center rounded-lg hover:bg-gray-100 text-gray-500">
          <Icon path={ICONS.chevron} className="w-3.5 h-3.5 rotate-90" stroke="currentColor" width={2.4} />
        </button>
        <p className="text-sm font-bold" style={{ color: NAVY }}>{monthLabel}</p>
        <button type="button" onClick={() => goMonth(1)} aria-label="Next month" className="w-7 h-7 flex items-center justify-center rounded-lg hover:bg-gray-100 text-gray-500">
          <Icon path={ICONS.chevron} className="w-3.5 h-3.5 -rotate-90" stroke="currentColor" width={2.4} />
        </button>
      </div>
      <div className="grid grid-cols-7 gap-1 text-center text-[10px] font-semibold text-gray-400 mb-1">
        {["MON", "TUE", "WED", "THU", "FRI", "SAT", "SUN"].map((d) => (
          <div key={d}>{d}</div>
        ))}
      </div>
      <div className="grid grid-cols-7 gap-1">
        {cells.map((day, i) => {
          if (day == null) return <div key={`empty-${i}`} />;
          const iso = toISO(day);
          const isSelected = iso === value;
          const isOutOfRange = (minDate && iso < minDate) || (maxDate && iso > maxDate);
          return (
            <button
              type="button"
              key={iso}
              disabled={isOutOfRange}
              onClick={() => onChange(iso)}
              className={`w-8 h-8 rounded-lg text-xs font-semibold transition ${
                isSelected ? "text-white" : isOutOfRange ? "text-gray-300 cursor-not-allowed" : "text-gray-700 hover:bg-blue-50"
              }`}
              style={isSelected ? { backgroundColor: PRIMARY } : undefined}
            >
              {day}
            </button>
          );
        })}
      </div>
    </div>
  );
}
